// src/components/LootQueueArena.tsx
// ============================================================================
// "LOOT QUEUE ARENA" — a throwaway party game for raiders waiting on loot.
//
// HARD CONSTRAINTS (LOOT_QUEUE_ARENA_SPEC.md §1 — DO NOT VIOLATE):
//   1. Client-heavy, server-light. ALL game logic (draft, RNG, combat, bracket)
//      runs on the HOST's browser. The server (arena_ws.js) is a dumb relay.
//   2. Zero database usage. No reads, no writes, no migrations, no tables.
//   3. No persistence of any kind — no localStorage/sessionStorage/cookies.
//      The only URL state is the session id in the path (/arena/<id>).
//   4. NEVER use ilvl. It is not read, stored, displayed, or factored into power.
//      It is intentionally absent from ArenaPlayer and the power model below.
//   5. Cheap: reuses the existing WebSocket. CSS animations, not per-frame React.
//      Every timer/socket is cleaned up on unmount.
//   6. No host migration / no reconnection recovery. Host disconnect ends it.
//
// HOST-SIDE DATA MAPPING (§5, §13.4) — candidates → ArenaPlayer[], ZERO new fetches.
// PlannerPage maps its already-loaded `candidates`/roster (HeuristicCharacter &
// { wclScore?: number }) into ArenaPlayer with:
//     name        ← character name        (REQUIRED)
//     className   ← metadata.class_name    (REQUIRED, for color + synergy checks)
//     specs       ← resolveSpecEntries(...) → [{label, role}]; a card rolls ONE at random
//     role        ← chosen spec's role (random per card) or resolveRoleLabels(...)[0]
//     parse       ← wclScore (0–100)             (optional, neutral default 50 — the card's power)
//     icon        ← metadata.render_url (Battle.net character render), if available
//     ilvl/BiS    ← *** NOT USED — ilvl never, BiS unavailable host-side (needs a fetch) ***
// The host broadcasts only the live board/teams to joiners; joiners build nothing.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API_URL } from "../config";

// ----------------------------------------------------------------------------
// Types (§5, §8)
// ----------------------------------------------------------------------------

export interface ArenaPlayer {
  id: string;
  name: string;
  className: string; // for color + synergy checks
  role?: "Tank" | "Healer" | "Melee DPS" | "Ranged DPS" | string | null; // default/fallback role
  // Every spec the player plays. A dealt card rolls ONE at random (even odds) and
  // takes that spec's role. e.g. a Fury/Prot warrior → 50% Melee, 50% Tank.
  specs?: { label: string; role: string }[];
  parse?: number | null; // 0–100, from wclScore. optional — this IS the card's power
  icon?: string | null; // character avatar (roster render_url), optional
  // NO ilvl, NO BiS. Do not add either.
}

export interface DraftCard {
  cardId: string; // unique per dealt card instance
  id: string; // owning ArenaPlayer id (for de-dupe / synergy)
  name: string;
  className: string;
  role?: string | null;     // the role of this card's rolled spec
  spec?: string | null;     // the spec rolled for this card instance (e.g. "Fury")
  parse?: number | null;
  icon?: string | null; // character avatar (roster render_url), optional
  modifier?: Modifier | null; // rolled rare (fixed for the whole game)
  isAI?: boolean;
}

export type Rarity = "common" | "rare" | "epic";

// Combat actives, resolved on the host in resolveMatch (epics, mostly).
export type CombatKind =
  | "bloodlust"     // +35% team power in round 1
  | "first_strike"  // win the opening round outright
  | "reroll_loss"   // once per fight, re-roll the first round you lose
  | "counterspell"  // enemy team starts 20% weaker
  | "last_stand";   // when behind in a fight, +40% power that round

// A TARGETED passive effect — buffs allies or debuffs enemies, filtered by role
// (or self). No blanket "+10 to everyone": every aura hits a specific group.
export interface Aura {
  target: "ally" | "enemy"; // which team it affects
  roles?: string[];         // affected roles (undefined = any role on that team)
  selfOnly?: boolean;       // only the card that carries it
  kind: "flat" | "mult";    // +N or ×N
  value: number;
  desc: string;             // short human label, e.g. "−10% to enemy melee"
}

// A class ability. Common/Rare = a targeted Aura; Epic = a combat active.
// At most one per card, fixed for the whole game. Pure data → serializes to joiners.
export interface Modifier {
  id: string;
  name: string;
  flavor: string;
  classKey: string;        // owning class ("Warrior"…) or "Generic"
  rarity: Rarity;
  icon: string;            // wowhead icon slug → CDN url
  aura?: Aura;             // passive targeted buff/debuff
  combat?: CombatKind;     // active effect (epics)
}

// A STAGE MUTATOR — a global rule rolled at the start of each draft and in force
// for that whole stage's fights. Pure data → joiners apply the exact same maths.
// Effects are deliberately symmetric (both teams play under the same rule); which
// side benefits depends on the COMP they drafted, so it pairs with team synergy.
export interface Mutator {
  id: string;
  name: string;
  icon: string;                              // emoji shown on the banner
  desc: string;                              // one-line human rule
  rolePower?: { roles: string[]; mult: number }; // ×mult to cards of these roles
  flatAll?: number;                          // +N flat power to every card
  eliteMult?: number;                        // ×mult to cards parsing 75+
  edgeMult?: number;                         // scales the role type-counter swing
  jitter?: number;                           // per-round variance (default 0.05)
}

// A team-composition synergy that fired for a drafted trio (see teamSynergyResult).
export interface SynergyHit {
  name: string;
  icon: string;
  desc: string;
  mult: number; // team-wide power multiplier this synergy contributes
}

interface LobbyPlayer {
  id: string;
  name: string;
  className: string;
  isHost: boolean;
  isAI?: boolean;
}

interface TeamRef {
  ownerId: string;
  ownerName: string;
  cards: DraftCard[];
  isAI?: boolean;
}

interface BracketMatch {
  id: string;
  teamA: TeamRef | null;
  teamB: TeamRef | null; // null (with teamA set) = a bye
  winner: "A" | "B" | null;
  score: [number, number] | null;
  done: boolean;
}

type Phase = "lobby" | "draft" | "bracket" | "gameover";

interface ArenaState {
  phase: Phase;
  players: LobbyPlayer[];
  // Each player drafts privately: they're offered a trio and keep one, which
  // refreshes the whole trio. No shared board, no contention ("first tap wins").
  offers: Record<string, DraftCard[]>;
  teams: Record<string, DraftCard[]>;
  draftEndsAt: number | null;
  draftRound: number; // current stage index (0-based). Everyone re-drafts each stage.
  draftingPlayerIds: string[]; // who must pick this draft (all real players, in Swiss)
  eliminatedIds: string[]; // unused in Swiss (kept empty); no one is eliminated
  // Swiss-style records — everyone keeps playing; winners get paired with winners.
  records: Record<string, { w: number; l: number }>;
  totalStages: number; // how many Swiss rounds (≈ ceil(log2(players)))
  bracket: { rounds: BracketMatch[][] } | null; // rounds[stage] = that stage's pairings
  currentMatchId: string | null;
  solo: boolean;
  soloStreak: number;
  soloFoe: TeamRef | null;
  champion: { ownerId: string; ownerName: string; cards: DraftCard[] } | null;
  hostName: string;
  mutator: Mutator | null; // the rule in force for the current draft/stage
}

// A fired ability shown as a spell-flash during a round.
interface RoundProc { name: string; icon: string; rarity: Rarity; side: "A" | "B" }
interface RoundResult {
  winner: "A" | "B";
  pair?: number;          // which card-duel index this round is (drives the bump)
  rerolledAway?: boolean; // a result that was re-rolled away (shown, but doesn't count)
  proc?: RoundProc;       // an ability that fired this round → flash it
}

interface MatchResultMsg {
  type: "arena:match_result";
  sessionId: string;
  matchId: string;
  teamA: TeamRef;
  teamB: TeamRef;
  rounds: RoundResult[];
  winner: "A" | "B";
  score: [number, number];
  mutator?: Mutator | null; // rule the fight was resolved under (for consistent display)
}

// ----------------------------------------------------------------------------
// Constants
// ----------------------------------------------------------------------------

const DRAFT_MS = 30000;
const TEAM_SIZE = 3;

const CLASS_COLORS: Record<string, string> = {
  Warrior: "#C79C6E", Paladin: "#F58CBA", Hunter: "#ABD473", Rogue: "#FFF569",
  Priest: "#FFFFFF", DeathKnight: "#C41F3B", Shaman: "#0070DE", Mage: "#40C7EB",
  Warlock: "#8787ED", Monk: "#00FF96", Druid: "#FF7D0A", DemonHunter: "#A330C9", Evoker: "#33937F",
};
const RINGER_NAMES = ["PugLord", "xXLeeroyXx", "AfkAndy", "NinjaLooter", "BackupBob", "FillerFred", "GuildBank Greg", "RandomDude", "DcOnPull"];
const RINGER_CLASSES = ["Warrior", "Mage", "Rogue", "Priest", "Hunter", "Druid", "Warlock", "Paladin", "Shaman"];
const AI_FOE_NAMES = ["The Tryhards", "Sweat Lords", "Parse Andys", "Wipe Crew", "Bench Warmers", "Loot Goblins", "Server First Wannabes", "Mythic Andies"];

function getClassColor(cn: string | null | undefined): string {
  if (!cn) return "#A3A3A3";
  const key = cn.replace(/\s+/g, "");
  if (CLASS_COLORS[key]) return CLASS_COLORS[key];
  const found = Object.keys(CLASS_COLORS).find((k) => k.toLowerCase() === key.toLowerCase());
  return found ? CLASS_COLORS[found] : "#A3A3A3";
}

function genId(prefix = "id"): string {
  try {
    // crypto.randomUUID is available in modern browsers (https/localhost).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const c: any = (typeof crypto !== "undefined" ? crypto : null);
    if (c && typeof c.randomUUID === "function") return `${prefix}_${c.randomUUID()}`;
  } catch { /* ignore */ }
  return `${prefix}_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// ----------------------------------------------------------------------------
// Class-ability registry — 3 per class (Common / Rare / Epic), themed on the
// Classic/TBC kit. Commons & Rares are passive power; Epics are combat actives.
// Rolled on the HOST so everyone sees the same abilities. Real WoW icons via the
// Wowhead CDN (missing slugs fall back to a rarity gem). Roll odds:
//   Epic 5%  ·  Rare 25%  ·  Common 50%  ·  nothing 20%.
// ----------------------------------------------------------------------------

const ICON_BASE = "https://wow.zamimg.com/images/wow/icons/large/";
function iconUrl(slug: string): string { return `${ICON_BASE}${slug}.jpg`; }

export const RARITY_META: Record<Rarity, { label: string; color: string }> = {
  common: { label: "Common", color: "#b9c0cc" },
  rare:   { label: "Rare",   color: "#3b82f6" },
  epic:   { label: "Epic",   color: "#a335ee" },
};

function combatLabel(kind: CombatKind): string {
  switch (kind) {
    case "bloodlust": return "Haste burst — your team +30% in round 1";
    case "first_strike": return "Stealth opener — win the opening round outright";
    case "reroll_loss": return "Battle res — once per fight, re-roll a round you lose";
    case "counterspell": return "Silence the enemy's strongest card (its ability off)";
    case "last_stand": return "When behind in a fight, +40% power that round";
  }
}

const MELEE = ["Melee DPS"];
const RANGED = ["Ranged DPS"];
const MELEE_TANK = ["Melee DPS", "Tank"];

// Each rarity now holds a POOL — a card rolls a rarity, then a random ability from
// that class's pool for it. More rares per class = more variety and combinations.
type AbilitySet = { common: Modifier[]; rare: Modifier[]; epic: Modifier[] };

function ab(id: string, classKey: string, rarity: Rarity, icon: string, name: string, flavor: string, opts: Partial<Modifier> = {}): Modifier {
  return { id, classKey, rarity, icon, name, flavor, ...opts };
}
// Targeted aura builders. buff* = help allies, hex* = weaken enemies, self = own card.
const buff = (roles: string[] | undefined, kind: "flat" | "mult", value: number, desc: string): { aura: Aura } => ({ aura: { target: "ally", roles, kind, value, desc } });
const hex = (roles: string[] | undefined, kind: "flat" | "mult", value: number, desc: string): { aura: Aura } => ({ aura: { target: "enemy", roles, kind, value, desc } });
const self = (kind: "flat" | "mult", value: number, desc: string): { aura: Aura } => ({ aura: { target: "ally", selfOnly: true, kind, value, desc } });

// Each class: a pool of targeted Commons + Rares (auras), and Epic combat actives.
// Multiple entries per rarity → the same class can show very different cards.
const CLASS_ABILITIES: Record<string, AbilitySet> = {
  Warrior: {
    common: [
      ab("warr_c", "Warrior", "common", "ability_warrior_battleshout", "Battle Shout", "AP for the melee. Casters get nothing, as usual.", buff(MELEE, "flat", 10, "+10 to melee allies")),
      ab("warr_c2", "Warrior", "common", "ability_warrior_commandingshout", "Commanding Shout", "Extra health for the whole front line.", buff(MELEE_TANK, "flat", 8, "+8 to melee & tank allies")),
    ],
    rare: [
      ab("warr_r", "Warrior", "rare", "ability_warrior_warcry", "Demoralizing Shout", "Enemy melee swing like they're underwater.", hex(MELEE_TANK, "mult", 0.88, "−12% to enemy melee & tanks")),
      ab("warr_r2", "Warrior", "rare", "ability_warrior_sunder", "Sunder Armor", "Strips the enemy tank's plating layer by layer.", hex(["Tank"], "mult", 0.84, "−16% to enemy tanks")),
      ab("warr_r3", "Warrior", "rare", "ability_golemthunderclap", "Intimidating Shout", "Scatters the enemy casters in a panic.", hex(RANGED, "mult", 0.90, "−10% to enemy ranged")),
    ],
    epic: [
      ab("warr_e", "Warrior", "epic", "ability_warrior_shieldwall", "Last Stand", "Refuses to die. Hits hardest with its back to the wall.", { combat: "last_stand" }),
      ab("warr_e2", "Warrior", "epic", "ability_criticalstrike", "Recklessness", "All offense, no defense. Comes out swinging first.", { combat: "first_strike" }),
    ],
  },
  Paladin: {
    common: [
      ab("pala_c", "Paladin", "common", "spell_holy_greaterblessingofkings", "Blessing of Might", "Hands the melee a fat slab of attack power.", buff(MELEE_TANK, "flat", 9, "+9 to melee allies")),
      ab("pala_c2", "Paladin", "common", "spell_holy_devotionaura", "Devotion Aura", "Armor for the tank so it eats hits all day.", buff(["Tank"], "flat", 12, "+12 to tank allies")),
    ],
    rare: [
      ab("pala_r", "Paladin", "rare", "spell_holy_searinglight", "Beacon of Light", "Pumps the healers so they out-heal the incoming.", buff(["Healer"], "flat", 16, "+16 to healer allies")),
      ab("pala_r2", "Paladin", "rare", "spell_holy_sealofmight", "Hammer of Justice", "Stuns an enemy melee mid-swing.", hex(MELEE, "mult", 0.87, "−13% to enemy melee")),
    ],
    epic: [
      ab("pala_e", "Paladin", "epic", "spell_holy_layonhands", "Lay on Hands", "Full heal from zero. Clutch incarnate.", { combat: "reroll_loss" }),
      ab("pala_e2", "Paladin", "epic", "spell_holy_divineshield", "Divine Shield", "Bubble up when it matters. Untouchable while behind.", { combat: "last_stand" }),
    ],
  },
  Hunter: {
    common: [
      ab("hunt_c", "Hunter", "common", "ability_hunter_longarm", "Trueshot Aura", "Ranged DPS perks up across the raid.", buff(RANGED, "flat", 10, "+10 to ranged allies")),
      ab("hunt_c2", "Hunter", "common", "ability_hunter_beastsoothe", "Aspect of the Hawk", "Personal ranged attack power. Pew pew.", self("flat", 11, "+11 to self")),
    ],
    rare: [
      ab("hunt_r", "Hunter", "rare", "ability_hunter_snipershot", "Concussive Shot", "Kites the enemy healers out of position.", hex(["Healer"], "mult", 0.85, "−15% to enemy healers")),
      ab("hunt_r2", "Hunter", "rare", "ability_hunter_aimedshot", "Aimed Shot", "A mortal wound on the enemy healer — it can't keep up.", hex(["Healer"], "mult", 0.80, "−20% to enemy healers")),
      ab("hunt_r3", "Hunter", "rare", "ability_rogue_trip", "Wing Clip", "Hamstrings the enemy melee so they fall behind.", hex(MELEE, "mult", 0.90, "−10% to enemy melee")),
    ],
    epic: [
      ab("hunt_e", "Hunter", "epic", "ability_rogue_feigndeath", "Feign Death", "Drops a loss and walks it off. Cheating? Adjacent.", { combat: "reroll_loss" }),
      ab("hunt_e2", "Hunter", "epic", "ability_hunter_misdirection", "Misdirection", "Pet opens before the pull — free first round.", { combat: "first_strike" }),
    ],
  },
  Rogue: {
    common: [
      ab("rog_c", "Rogue", "common", "ability_rogue_slicedice", "Slice and Dice", "Pure selfish uptime. More swings, more numbers.", self("flat", 13, "+13 to self")),
      ab("rog_c2", "Rogue", "common", "ability_rogue_eviscerate", "Eviscerate", "Dumps combo points into one fat hit.", self("flat", 11, "+11 to self")),
    ],
    rare: [
      ab("rog_r", "Rogue", "rare", "ability_warrior_riposte", "Expose Armor", "Shreds the enemy tank's armor to ribbons.", hex(["Tank"], "mult", 0.82, "−18% to enemy tanks")),
      ab("rog_r2", "Rogue", "rare", "ability_rogue_kidneyshot", "Kidney Shot", "Stunlocks an enemy caster out of the fight.", hex(RANGED, "mult", 0.85, "−15% to enemy ranged")),
      ab("rog_r3", "Rogue", "rare", "ability_warrior_punishingblow", "Blade Flurry", "Goes berserk — its own numbers spike hard.", self("mult", 1.25, "×1.25 to self")),
    ],
    epic: [
      ab("rog_e", "Rogue", "epic", "ability_rogue_ambush", "Ambush", "Opens from stealth. You lost round one before it started.", { combat: "first_strike" }),
      ab("rog_e2", "Rogue", "epic", "ability_vanish", "Vanish", "Drops aggro and resets a bad round in its favor.", { combat: "reroll_loss" }),
    ],
  },
  Priest: {
    common: [
      ab("prie_c", "Priest", "common", "spell_holy_wordfortitude", "Power Word: Fortitude", "Stamina for the front line so they stop dying.", buff(["Tank"], "flat", 14, "+14 to tank allies")),
      ab("prie_c2", "Priest", "common", "spell_holy_powerinfusion", "Power Infusion", "Hastes a caster ally into overdrive.", buff(RANGED, "flat", 13, "+13 to ranged allies")),
    ],
    rare: [
      ab("prie_r", "Priest", "rare", "spell_shadow_shadowwordpain", "Shadow Word: Pain", "A creeping dot that wears the enemy casters down.", hex(RANGED, "mult", 0.88, "−12% to enemy ranged")),
      ab("prie_r2", "Priest", "rare", "spell_shadow_siphonmana", "Mind Flay", "Channels into an enemy melee, slowing it to a crawl.", hex(MELEE, "mult", 0.88, "−12% to enemy melee")),
      ab("prie_r3", "Priest", "rare", "spell_shadow_unholyfrenzy", "Vampiric Embrace", "Feeds on the fight — heals into its own damage.", self("mult", 1.18, "×1.18 to self")),
    ],
    epic: [
      ab("prie_e", "Priest", "epic", "spell_holy_resurrection", "Spirit of Redemption", "Dies, then keeps healing. Refuses to accept the loss.", { combat: "reroll_loss" }),
      ab("prie_e2", "Priest", "epic", "spell_holy_painsupression", "Pain Suppression", "Slams the brakes when behind — survives the swing.", { combat: "last_stand" }),
    ],
  },
  Shaman: {
    common: [
      ab("sham_c", "Shaman", "common", "spell_nature_windfury", "Windfury Totem", "Melee get extra swings. Chaotic, glorious.", buff(MELEE, "flat", 11, "+11 to melee allies")),
      ab("sham_c2", "Shaman", "common", "spell_nature_earthbindtotem", "Strength of Earth", "Raw stats for everyone up front.", buff(MELEE_TANK, "flat", 9, "+9 to melee & tank allies")),
    ],
    rare: [
      ab("sham_r", "Shaman", "rare", "spell_frost_frostshock", "Frost Shock", "Roots the enemy melee mid-charge.", hex(MELEE, "mult", 0.88, "−12% to enemy melee")),
      ab("sham_r2", "Shaman", "rare", "spell_nature_earthshock", "Earth Shock", "Interrupts the enemy caster line cold.", hex(RANGED, "mult", 0.88, "−12% to enemy ranged")),
      ab("sham_r3", "Shaman", "rare", "spell_nature_manaregentotem", "Mana Tide Totem", "Floods the healers' mana bars.", buff(["Healer"], "flat", 18, "+18 to healer allies")),
    ],
    epic: [
      ab("sham_e", "Shaman", "epic", "spell_nature_bloodlust", "Bloodlust", "30% haste for the whole party. Pop it on the pull.", { combat: "bloodlust" }),
      ab("sham_e2", "Shaman", "epic", "spell_nature_reincarnation", "Reincarnation", "Self-res. Eats a loss and stands right back up.", { combat: "reroll_loss" }),
    ],
  },
  Mage: {
    common: [
      ab("mage_c", "Mage", "common", "spell_holy_magicalsentry", "Arcane Intellect", "Crit and mana for the caster line.", buff(RANGED, "flat", 10, "+10 to ranged allies")),
      ab("mage_c2", "Mage", "common", "spell_frost_coldhearted", "Icy Veins", "Personal haste burst. The numbers blur.", self("mult", 1.20, "×1.20 to self")),
    ],
    rare: [
      ab("mage_r", "Mage", "rare", "spell_frost_frostbolt02", "Frostbolt", "Chills the enemy melee to the bone — they slow down.", hex(MELEE, "mult", 0.90, "−10% to enemy melee")),
      ab("mage_r2", "Mage", "rare", "spell_nature_polymorph", "Polymorph", "Sheeps the enemy healer clean out of the fight.", hex(["Healer"], "mult", 0.82, "−18% to enemy healers")),
      ab("mage_r3", "Mage", "rare", "spell_fire_soulburn", "Scorch", "Stacks a burn on the enemy casters.", hex(RANGED, "mult", 0.90, "−10% to enemy ranged")),
    ],
    epic: [
      ab("mage_e", "Mage", "epic", "spell_frost_iceshock", "Counterspell", "Interrupts and silences the enemy's biggest threat.", { combat: "counterspell" }),
      ab("mage_e2", "Mage", "epic", "spell_frost_frost", "Ice Block", "Phases out a losing exchange and tries again.", { combat: "reroll_loss" }),
    ],
  },
  Warlock: {
    common: [
      ab("lock_c", "Warlock", "common", "spell_shadow_ragingscream", "Fel Armor", "Selfish demonic plating. Lives, deals, repeats.", self("flat", 12, "+12 to self")),
      ab("lock_c2", "Warlock", "common", "spell_shadow_metamorphosis", "Demonic Pact", "Spell power bleeds out to the caster line.", buff(RANGED, "flat", 10, "+10 to ranged allies")),
    ],
    rare: [
      ab("lock_r", "Warlock", "rare", "spell_shadow_curseofachimonde", "Curse of the Elements", "The enemy casters' own spells turn on them.", hex(RANGED, "mult", 0.86, "−14% to enemy ranged")),
      ab("lock_r2", "Warlock", "rare", "spell_shadow_curseoftounges", "Curse of Tongues", "The enemy healers cast at half speed.", hex(["Healer"], "mult", 0.85, "−15% to enemy healers")),
      ab("lock_r3", "Warlock", "rare", "spell_shadow_curseofmannoroth", "Curse of Weakness", "Saps the enemy melee's strength away.", hex(MELEE, "mult", 0.88, "−12% to enemy melee")),
    ],
    epic: [
      ab("lock_e", "Warlock", "epic", "spell_shadow_soulgem", "Soulstone", "Pre-placed insurance. Dies, comes back, keeps going.", { combat: "reroll_loss" }),
      ab("lock_e2", "Warlock", "epic", "spell_shadow_shadowfury", "Shadowfury", "Slams the field first — opening round is theirs.", { combat: "first_strike" }),
    ],
  },
  Druid: {
    common: [
      ab("dru_c", "Druid", "common", "spell_nature_regeneration", "Mark of the Wild", "Stats and armor that keep the healers standing.", buff(["Healer"], "flat", 13, "+13 to healer allies")),
      ab("dru_c2", "Druid", "common", "spell_nature_unyeildingstamina", "Leader of the Pack", "Crit aura that lights up the melee.", buff(MELEE, "flat", 11, "+11 to melee allies")),
    ],
    rare: [
      ab("dru_r", "Druid", "rare", "ability_druid_mangle2", "Mangle", "Goes feral and pads its own damage meter.", self("mult", 1.20, "×1.20 to self")),
      ab("dru_r2", "Druid", "rare", "spell_nature_faeriefire", "Faerie Fire", "Peels the enemy tank's armor right off.", hex(["Tank"], "mult", 0.85, "−15% to enemy tanks")),
      ab("dru_r3", "Druid", "rare", "spell_nature_lightning", "Innervate", "Dumps a full mana bar into a healer.", buff(["Healer"], "flat", 16, "+16 to healer allies")),
    ],
    epic: [
      ab("dru_e", "Druid", "epic", "spell_nature_reincarnation", "Rebirth", "Battle res. Lose a round? Drag a teammate back and run it again.", { combat: "reroll_loss" }),
      ab("dru_e2", "Druid", "epic", "spell_nature_tranquility", "Tranquility", "Mass heal that holds the line when cornered.", { combat: "last_stand" }),
    ],
  },
  Generic: {
    common: [
      ab("gen_c", "Generic", "common", "inv_potion_54", "Flask of the Pull", "Always flasked. Just a little personal edge.", self("flat", 10, "+10 to self")),
      ab("gen_c2", "Generic", "common", "inv_stone_04", "Healthstone", "Pops a stone and shrugs off a bad moment.", self("flat", 8, "+8 to self")),
    ],
    rare: [
      ab("gen_r", "Generic", "rare", "inv_alchemy_elixir_04", "Pre-Pot", "Popped a pot before the pull. Degenerate. Respect.", self("mult", 1.18, "×1.18 to self")),
      ab("gen_r2", "Generic", "rare", "inv_drink_05", "World Buff", "Rolled in flagged with raid buffs. Disgusting numbers.", self("mult", 1.22, "×1.22 to self")),
    ],
    epic: [
      ab("gen_e", "Generic", "epic", "inv_misc_pocketwatch_01", "Big Pull Energy", "No plan, swings hardest when cornered.", { combat: "last_stand" }),
      ab("gen_e2", "Generic", "epic", "inv_potion_31", "Combat Drugs", "Whatever's in that vial, it hits the pull at full tilt.", { combat: "bloodlust" }),
    ],
  },
};

function classAbilitySet(className: string | null | undefined): AbilitySet {
  if (!className) return CLASS_ABILITIES.Generic;
  if (CLASS_ABILITIES[className]) return CLASS_ABILITIES[className];
  const key = className.replace(/\s+/g, "");
  return CLASS_ABILITIES[key] || CLASS_ABILITIES.Generic;
}

function pickFrom<T>(arr: T[]): T | null { return arr.length ? arr[Math.floor(Math.random() * arr.length)] : null; }

// Roll one ability for a card by class. 5% epic, 25% rare, 50% common, 20% none.
// The rarity picks a POOL, then one ability is drawn from it at random.
function rollAbility(className: string | null | undefined): Modifier | null {
  const r = Math.random();
  const set = classAbilitySet(className);
  if (r < 0.05) return pickFrom(set.epic) || pickFrom(set.rare) || null;
  if (r < 0.30) return pickFrom(set.rare) || pickFrom(set.common) || null;
  if (r < 0.80) return pickFrom(set.common) || null;
  return null;
}

// ----------------------------------------------------------------------------
// Power model — NO ILVL, NO BiS, NO flat role bonus. Computed on the host.
//   base power = parse (?? 50 neutral). Then TARGETED auras apply: ally auras
//   from teammates (buffs) and enemy auras from the opposing team (debuffs),
//   filtered by the card's role. Power is therefore matchup-aware — an enemy
//   Frost Mage genuinely chills YOUR melee. Role also gives a soft type-counter
//   nudge during combat (see resolveMatch). Auras are pure data → joiners match.
// ----------------------------------------------------------------------------

function cardBasePower(c: DraftCard): number {
  return c.parse ?? 50;
}

// Does aura `a` carried by `provider` (on `provider`'s team) hit `card`?
function auraHits(a: Aura, provider: DraftCard, card: DraftCard, sameTeam: boolean): boolean {
  if (a.target === "ally") {
    if (!sameTeam) return false;
    if (a.selfOnly) return provider === card;
    return !a.roles || a.roles.includes(card.role ?? "");
  }
  // enemy debuff: provider is on the opposing team to `card`
  if (sameTeam) return false;
  return !a.roles || a.roles.includes(card.role ?? "");
}

// A card's flat/mult contribution from the active stage mutator (per-card effects).
function mutatorCardEffect(card: DraftCard, mut?: Mutator | null): { flat: number; mult: number } {
  if (!mut) return { flat: 0, mult: 1 };
  let flat = 0, mult = 1;
  if (mut.flatAll) flat += mut.flatAll;
  if (mut.rolePower && mut.rolePower.roles.includes(card.role ?? "")) mult *= mut.rolePower.mult;
  if (mut.eliteMult && (card.parse ?? 0) >= 75) mult *= mut.eliteMult;
  return { flat, mult };
}

// Full breakdown of a card's power facing `enemy` (enemy = [] in the draft view).
// `silenced` = cardIds whose ability is suppressed (e.g. by Counterspell).
// `mut` = active stage mutator (per-card role/flat/elite tweaks), if any.
function cardPowerBreakdown(card: DraftCard, team: DraftCard[], enemy: DraftCard[] = [], silenced?: Set<string>, mut?: Mutator | null): { base: number; flat: number; mult: number; final: number } {
  const base = cardBasePower(card);
  let flat = 0, mult = 1;
  for (const p of team) {
    if (silenced?.has(p.cardId)) continue;
    const a = p.modifier?.aura;
    if (a && auraHits(a, p, card, true)) { if (a.kind === "flat") flat += a.value; else mult *= a.value; }
  }
  for (const p of enemy) {
    if (silenced?.has(p.cardId)) continue;
    const a = p.modifier?.aura;
    if (a && auraHits(a, p, card, false)) { if (a.kind === "flat") flat += a.value; else mult *= a.value; }
  }
  const me = mutatorCardEffect(card, mut);
  flat += me.flat; mult *= me.mult;
  const final = Math.max(1, (base + flat) * mult);
  return { base, flat, mult, final };
}

function cardPower(card: DraftCard, team: DraftCard[], enemy: DraftCard[] = [], silenced?: Set<string>, mut?: Mutator | null): number {
  return cardPowerBreakdown(card, team, enemy, silenced, mut).final;
}

// ----------------------------------------------------------------------------
// Team composition synergies — the draft now rewards SHAPE, not just raw parse.
// Pure function of a team's cards → joiners compute the identical bonus. Multiple
// synergies stack multiplicatively (committing hard to one comp pays off).
// ----------------------------------------------------------------------------
function teamSynergyResult(cards: DraftCard[]): { mult: number; hits: SynergyHit[] } {
  const hits: SynergyHit[] = [];
  if (cards.length < TEAM_SIZE) return { mult: 1, hits }; // only score a finished trio
  const roles = cards.map((c) => c.role ?? "");
  const classes = cards.map((c) => c.className || "Unknown");
  const hasTank = roles.includes("Tank");
  const hasHealer = roles.includes("Healer");
  const hasDps = roles.some((r) => r === "Melee DPS" || r === "Ranged DPS");
  const classCounts: Record<string, number> = {};
  classes.forEach((c) => { classCounts[c] = (classCounts[c] || 0) + 1; });
  const maxClass = Math.max(...Object.values(classCounts));

  if (roles.every((r) => r === "Melee DPS")) hits.push({ name: "Melee Cleave", icon: "⚔️", desc: "All-melee — relentless pressure", mult: 1.15 });
  else if (roles.every((r) => r === "Ranged DPS")) hits.push({ name: "Caster Cleave", icon: "🔮", desc: "All-ranged — nuke squad", mult: 1.15 });
  else if (hasTank && hasHealer && hasDps) hits.push({ name: "Holy Trinity", icon: "⚖️", desc: "Tank + Healer + DPS — textbook", mult: 1.12 });

  if (maxClass === TEAM_SIZE) hits.push({ name: "Class Stacked", icon: "🎭", desc: `Triple ${classes[0]} — bring the buff`, mult: 1.15 });
  else if (maxClass === 2) hits.push({ name: "Brothers in Arms", icon: "🤝", desc: "A matched class pair", mult: 1.06 });

  if (cards.every((c) => (c.parse ?? 0) >= 75)) hits.push({ name: "All-Stars", icon: "⭐", desc: "Every card parses purple+", mult: 1.12 });
  if (cards.filter((c) => c.modifier?.rarity === "epic").length >= 2) hits.push({ name: "Stacked Deck", icon: "💎", desc: "Two+ epic abilities", mult: 1.10 });

  const mult = hits.reduce((m, h) => m * h.mult, 1);
  return { mult, hits };
}

function teamPower(team: DraftCard[], enemy: DraftCard[] = [], silenced?: Set<string>, mut?: Mutator | null): number {
  const indiv = team.reduce((sum, c) => sum + cardPower(c, team, enemy, silenced, mut), 0);
  return indiv * teamSynergyResult(team).mult;
}

// ----------------------------------------------------------------------------
// Stage mutators — a global rule rolled each draft, in force for that stage's
// fights. Both teams play under it; which side it favours depends on their comp.
// ----------------------------------------------------------------------------
const MUTATORS: Mutator[] = [
  { id: "caster_supremacy", name: "Caster Supremacy", icon: "🔮", desc: "Ranged DPS deal +25%", rolePower: { roles: RANGED, mult: 1.25 } },
  { id: "blade_dance",      name: "Blade Dance",      icon: "⚔️", desc: "Melee DPS deal +25%",  rolePower: { roles: MELEE, mult: 1.25 } },
  { id: "tank_meta",        name: "Tank Meta",        icon: "🛡️", desc: "Tanks are juggernauts (+60%)", rolePower: { roles: ["Tank"], mult: 1.60 } },
  { id: "mana_drought",     name: "Mana Drought",     icon: "🩸", desc: "Healers run dry (−40%)", rolePower: { roles: ["Healer"], mult: 0.60 } },
  { id: "counter_culture",  name: "Counter Culture",  icon: "🔄", desc: "Type counters hit 3× harder", edgeMult: 3 },
  { id: "the_equalizer",    name: "The Great Equalizer", icon: "⚖️", desc: "+25 flat power to every card — anyone can win", flatAll: 25 },
  { id: "high_stakes",      name: "High Stakes",      icon: "🎲", desc: "Wild variance — upsets everywhere", jitter: 0.22 },
  { id: "prime_time",       name: "Prime Time",       icon: "⭐", desc: "Purple-parse cards (75+) deal +20%", eliteMult: 1.20 },
];
function rollMutator(): Mutator { return MUTATORS[Math.floor(Math.random() * MUTATORS.length)]; }

// ----------------------------------------------------------------------------
// Role type-counter (a soft nudge, NEVER decisive). The cycle:
//   Tank ▸ Melee ▸ Ranged ▸ Healer ▸ Tank
// Each round the dueling pair's roles give the favored side a small power bonus,
// tilting the coin flip — upsets stay very much possible.
// ----------------------------------------------------------------------------

const ROLE_COUNTERS: Record<string, string> = {
  "Tank": "Melee DPS",
  "Melee DPS": "Ranged DPS",
  "Ranged DPS": "Healer",
  "Healer": "Tank",
};
const ROLE_EDGE = 0.15; // max ±15% power swing from a favorable matchup — a nudge, not a verdict

// +1 if A's role counters B's, -1 if B's counters A's, 0 neutral / unknown role.
function roleEdge(aRole?: string | null, bRole?: string | null): number {
  if (!aRole || !bRole) return 0;
  if (ROLE_COUNTERS[aRole] === bRole) return 1;
  if (ROLE_COUNTERS[bRole] === aRole) return -1;
  return 0;
}

// ----------------------------------------------------------------------------
// Combat (§9) — chance-based, upsets must be possible. All rolls on the host.
// Each bo3 round is a duel between the cards at that round index (the bumping
// pair in the animation); team power is the base, role-counter is a soft nudge,
// and class-ability combat actives layer on top (procs surface as round notes).
// ----------------------------------------------------------------------------

function teamHasCombat(team: DraftCard[], kind: CombatKind, silenced?: Set<string>): boolean {
  return team.some((c) => c.modifier?.combat === kind && !silenced?.has(c.cardId));
}
// The ability of a given combat kind on a team (for the proc flash icon).
function combatAbility(team: DraftCard[], kind: CombatKind, silenced?: Set<string>): Modifier | undefined {
  return team.find((c) => c.modifier?.combat === kind && !silenced?.has(c.cardId))?.modifier || undefined;
}
function procOf(team: DraftCard[], kind: CombatKind, side: "A" | "B", silenced?: Set<string>): RoundProc | undefined {
  const m = combatAbility(team, kind, silenced);
  return m ? { name: m.name, icon: m.icon, rarity: m.rarity, side } : undefined;
}
// Counterspell hits the enemy's biggest play: an Epic carrier first, else the
// strongest aura carrier, else the strongest card outright.
function pickCounterTarget(team: DraftCard[]): string | null {
  const strongest = (list: DraftCard[]) => list.reduce((b, c) => (cardBasePower(c) > cardBasePower(b) ? c : b), list[0]);
  const epics = team.filter((c) => c.modifier?.combat);
  if (epics.length) return strongest(epics).cardId;
  const auras = team.filter((c) => c.modifier?.aura);
  if (auras.length) return strongest(auras).cardId;
  return team.length ? strongest(team).cardId : null;
}

// Base team powers for a matchup, with Counterspell silencing folded in. Shared
// by resolveMatch and matchWinChance / the scorecards so everything agrees.
function matchBases(teamA: DraftCard[], teamB: DraftCard[], mut?: Mutator | null): { baseA: number; baseB: number; silenced: Set<string> } {
  const silenced = new Set<string>();
  if (teamHasCombat(teamA, "counterspell")) { const t = pickCounterTarget(teamB); if (t) silenced.add(t); }
  if (teamHasCombat(teamB, "counterspell")) { const t = pickCounterTarget(teamA); if (t) silenced.add(t); }
  return { baseA: teamPower(teamA, teamB, silenced, mut), baseB: teamPower(teamB, teamA, silenced, mut), silenced };
}

// ----------------------------------------------------------------------------
// Combat — chance-based, upsets must be possible. All rolls on the host. Each
// bo3 round is a duel between the cards at that round index; team power is the
// base, role-counter a soft nudge, and combat actives layer on top. Procs are
// tagged onto rounds (→ spell flash); a re-roll emits the lost attempt first
// (rerolledAway, shown but not counted) then the kept result.
// ----------------------------------------------------------------------------

function resolveMatch(teamA: DraftCard[], teamB: DraftCard[], mut?: Mutator | null): { rounds: RoundResult[]; winner: "A" | "B"; score: [number, number] } {
  const { baseA, baseB, silenced } = matchBases(teamA, teamB, mut);
  const edgeScale = ROLE_EDGE * (mut?.edgeMult ?? 1);
  const jitter = mut?.jitter ?? 0.05;

  const aFirst = teamHasCombat(teamA, "first_strike", silenced);
  const bFirst = teamHasCombat(teamB, "first_strike", silenced);
  const aBlood = teamHasCombat(teamA, "bloodlust", silenced);
  const bBlood = teamHasCombat(teamB, "bloodlust", silenced);
  const aLast = teamHasCombat(teamA, "last_stand", silenced);
  const bLast = teamHasCombat(teamB, "last_stand", silenced);
  let aReroll = teamHasCombat(teamA, "reroll_loss", silenced);
  let bReroll = teamHasCombat(teamB, "reroll_loss", silenced);

  // Counterspell flash on the opening round, by whichever side cast it.
  const counterProc = silenced.size
    ? (teamHasCombat(teamA, "counterspell") ? procOf(teamA, "counterspell", "A") : procOf(teamB, "counterspell", "B"))
    : undefined;

  const flip = (pa: number, pb: number) => Math.random() < pa / (pa + pb || 1);
  const rounds: RoundResult[] = [];
  let aw = 0, bw = 0, i = 0;

  while (aw < 2 && bw < 2 && i < 8) {
    const pair = i; // dueling-card index (view clamps to team size)

    // First strike: the opening round is an automatic win (cancels if both have it).
    if (i === 0 && aFirst !== bFirst) {
      const side: "A" | "B" = aFirst ? "A" : "B";
      if (side === "A") aw++; else bw++;
      rounds.push({ winner: side, pair, proc: procOf(side === "A" ? teamA : teamB, "first_strike", side, silenced) });
      i++;
      continue;
    }

    const a = teamA.length ? teamA[i % teamA.length] : undefined;
    const b = teamB.length ? teamB[i % teamB.length] : undefined;
    const edge = roleEdge(a?.role, b?.role);
    let pa = baseA * (1 + Math.max(0, edge) * edgeScale);
    let pb = baseB * (1 + Math.max(0, -edge) * edgeScale);
    let proc: RoundProc | undefined = i === 0 ? counterProc : undefined;
    if (i === 0 && aBlood) { pa *= 1.30; proc = procOf(teamA, "bloodlust", "A", silenced); }
    if (i === 0 && bBlood) { pb *= 1.30; proc = procOf(teamB, "bloodlust", "B", silenced); }
    if (bw > aw && aLast) { pa *= 1.4; proc = procOf(teamA, "last_stand", "A", silenced); }
    if (aw > bw && bLast) { pb *= 1.4; proc = procOf(teamB, "last_stand", "B", silenced); }
    pa *= 1 + (Math.random() * 2 * jitter - jitter); // round variance (mutators can widen it)
    pb *= 1 + (Math.random() * 2 * jitter - jitter);

    let aWins = flip(pa, pb);
    // The losing side may re-roll the round once (battle res / clutch heal). The
    // failed attempt is emitted first so the animation shows the loss → rez → retry.
    const loserReroll = aWins ? bReroll : aReroll;
    if (loserReroll) {
      const rerollSide: "A" | "B" = aWins ? "B" : "A";
      rounds.push({ winner: aWins ? "A" : "B", pair, rerolledAway: true });
      if (aWins) bReroll = false; else aReroll = false;
      aWins = flip(pa, pb);
      proc = procOf(rerollSide === "A" ? teamA : teamB, "reroll_loss", rerollSide, silenced);
    }

    if (aWins) aw++; else bw++;
    rounds.push({ winner: aWins ? "A" : "B", pair, proc });
    i++;
  }
  if (aw < 2 && bw < 2) { if (baseA >= baseB) aw = 2; else bw = 2; } // safety
  return { rounds, winner: aw > bw ? "A" : "B", score: [aw, bw] };
}

// Pre-fight probability that team A takes the bo3 — uses the same bases (incl.
// Counterspell) + role-counter edges, minus jitter (symmetric). Display only.
function matchWinChance(teamA: DraftCard[], teamB: DraftCard[], mut?: Mutator | null): number {
  const { baseA, baseB } = matchBases(teamA, teamB, mut);
  const edgeScale = ROLE_EDGE * (mut?.edgeMult ?? 1);
  const p: number[] = [];
  for (let i = 0; i < 3; i++) {
    const a = teamA.length ? teamA[i % teamA.length] : undefined;
    const b = teamB.length ? teamB[i % teamB.length] : undefined;
    const edge = roleEdge(a?.role, b?.role);
    const pa = baseA * (1 + Math.max(0, edge) * edgeScale);
    const pb = baseB * (1 + Math.max(0, -edge) * edgeScale);
    p.push(pa / (pa + pb || 1));
  }
  const [p0, p1, p2] = p;
  // bo3, first to 2 — rounds resolve with p0, p1, p2 in order
  return p0 * p1 + p0 * (1 - p1) * p2 + (1 - p0) * p1 * p2;
}

// ----------------------------------------------------------------------------
// Card factories
// ----------------------------------------------------------------------------

function makeCard(p: ArenaPlayer): DraftCard {
  // Roll ONE spec at random (even odds). Its role is what this card plays as.
  let role = p.role ?? null;
  let spec: string | null = null;
  if (p.specs && p.specs.length) {
    const chosen = p.specs[Math.floor(Math.random() * p.specs.length)];
    spec = chosen.label || null;
    if (chosen.role) role = chosen.role;
  }
  return {
    cardId: genId("c"),
    id: p.id,
    name: p.name,
    className: p.className || "Unknown",
    role,
    spec,
    parse: p.parse ?? null,
    icon: p.icon ?? null,
    modifier: rollAbility(p.className),
  };
}

function makeRinger(): DraftCard {
  const name = RINGER_NAMES[Math.floor(Math.random() * RINGER_NAMES.length)];
  const className = RINGER_CLASSES[Math.floor(Math.random() * RINGER_CLASSES.length)];
  return {
    cardId: genId("ringer"),
    id: genId("ai"),
    name,
    className,
    role: ["Tank", "Healer", "Melee DPS", "Ranged DPS"][Math.floor(Math.random() * 4)],
    parse: 35 + Math.floor(Math.random() * 40),
    modifier: rollAbility(className),
    isAI: true,
  };
}

// ----------------------------------------------------------------------------
// One-time injected styles (CSS animations — cheap, not per-frame React)
// ----------------------------------------------------------------------------

const STYLE_ID = "lqa-styles";
function ensureStyles() {
  if (typeof document === "undefined" || document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
@keyframes lqa-pop { 0%{ transform: scale(.6) translateY(8px); opacity:0 } 60%{ transform: scale(1.06) } 100%{ transform: scale(1) translateY(0); opacity:1 } }
@keyframes lqa-bumpL { 0%{ transform: translateX(0) } 45%{ transform: translateX(38%) scale(1.08) } 55%{ transform: translateX(34%) scale(1.05) rotate(-2deg) } 100%{ transform: translateX(0) } }
@keyframes lqa-bumpR { 0%{ transform: translateX(0) } 45%{ transform: translateX(-38%) scale(1.08) } 55%{ transform: translateX(-34%) scale(1.05) rotate(2deg) } 100%{ transform: translateX(0) } }
@keyframes lqa-foil { 0%{ background-position: 0% 50% } 100%{ background-position: 200% 50% } }
@keyframes lqa-shock { 0%{ transform: scale(.2); opacity:.8 } 100%{ transform: scale(2.4); opacity:0 } }
@keyframes lqa-confetti { 0%{ transform: translateY(-10vh) rotate(0); opacity:1 } 100%{ transform: translateY(110vh) rotate(720deg); opacity:.9 } }
@keyframes lqa-pulse { 0%,100%{ box-shadow:0 0 0 0 rgba(245,158,11,.6) } 50%{ box-shadow:0 0 0 6px rgba(245,158,11,0) } }
@keyframes lqa-powerPop { 0%{ transform: scale(.5); opacity:0 } 55%{ transform: scale(1.18) } 100%{ transform: scale(1); opacity:1 } }
@keyframes lqa-rareGlow { 0%,100%{ box-shadow: 0 0 10px rgba(245,158,11,.45), 0 0 0 1px rgba(245,158,11,.35) } 50%{ box-shadow: 0 0 22px rgba(245,158,11,.75), 0 0 36px rgba(120,200,255,.25), 0 0 0 1px rgba(255,215,120,.55) } }
@keyframes lqa-clash { 0%,100%{ filter: brightness(1) } 40%{ filter: brightness(1.35) } }
@keyframes lqa-spellflash { 0%{ transform: scale(.3) rotate(-12deg); opacity:0 } 25%{ transform: scale(1.15) rotate(0deg); opacity:1 } 70%{ transform: scale(1) rotate(0deg); opacity:1 } 100%{ transform: scale(1.4); opacity:0 } }
@keyframes lqa-spellring { 0%{ transform: scale(.4); opacity:.9 } 100%{ transform: scale(2.6); opacity:0 } }
@keyframes lqa-vsplash { 0%{ opacity:0 } 12%{ opacity:1 } 82%{ opacity:1 } 100%{ opacity:0 } }
@keyframes lqa-slideL { 0%{ transform: translateX(-120%); opacity:0 } 55%{ transform: translateX(6%); opacity:1 } 70%{ transform: translateX(0) } 100%{ transform: translateX(0); opacity:1 } }
@keyframes lqa-slideR { 0%{ transform: translateX(120%); opacity:0 } 55%{ transform: translateX(-6%); opacity:1 } 70%{ transform: translateX(0) } 100%{ transform: translateX(0); opacity:1 } }
@keyframes lqa-vspop { 0%{ transform: scale(0) rotate(-25deg); opacity:0 } 50%{ transform: scale(1.5) rotate(8deg); opacity:1 } 70%{ transform: scale(.9) rotate(-3deg) } 100%{ transform: scale(1.15) rotate(0); opacity:1 } }
@keyframes lqa-roundpop { 0%{ transform: translateY(8px) scale(.7); opacity:0 } 30%{ transform: translateY(0) scale(1.1); opacity:1 } 80%{ transform: scale(1); opacity:1 } 100%{ opacity:0 } }
@keyframes lqa-stamp { 0%{ transform: scale(2.4) rotate(-18deg); opacity:0 } 60%{ transform: scale(.9) rotate(-12deg); opacity:1 } 100%{ transform: scale(1) rotate(-12deg); opacity:1 } }
@keyframes lqa-winsplash { 0%{ opacity:0 } 8%{ opacity:1 } 88%{ opacity:1 } 100%{ opacity:0 } }
@keyframes lqa-wincrown { 0%{ transform: scale(.25) translateY(24px); opacity:0 } 50%{ transform: scale(1.06) translateY(0); opacity:1 } 70%{ transform: scale(.94) } 100%{ transform: scale(1) translateY(0); opacity:1 } }
@keyframes lqa-namepulse { 0%,100%{ filter: brightness(1) } 50%{ filter: brightness(1.35) } }
.lqa-pop { animation: lqa-pop .35s cubic-bezier(.2,.8,.3,1.2) both; }
.lqa-powerPop { animation: lqa-powerPop .45s cubic-bezier(.2,.8,.3,1.3) both; }
.lqa-rareGlow { animation: lqa-rareGlow 1.6s ease-in-out infinite; }
.lqa-clash { animation: lqa-clash .55s ease-in-out; }
.lqa-spellflash { animation: lqa-spellflash 1.1s ease-out both; }
.lqa-spellring { animation: lqa-spellring .9s ease-out forwards; }
.lqa-vsplash { animation: lqa-vsplash var(--lqa-intro,1700ms) ease-in-out both; }
.lqa-slideL { animation: lqa-slideL .6s cubic-bezier(.2,.8,.3,1.2) both; }
.lqa-slideR { animation: lqa-slideR .6s cubic-bezier(.2,.8,.3,1.2) both; }
.lqa-vspop { animation: lqa-vspop .7s cubic-bezier(.2,.8,.3,1.3) both; }
.lqa-roundpop { animation: lqa-roundpop 1.4s ease-out both; }
.lqa-stamp { animation: lqa-stamp .5s cubic-bezier(.2,.8,.3,1.4) both; }
.lqa-winsplash { animation: lqa-winsplash var(--lqa-banner,1300ms) ease-in-out both; }
.lqa-wincrown { animation: lqa-wincrown .7s cubic-bezier(.2,.8,.3,1.3) both; }
.lqa-namepulse { animation: lqa-namepulse 1.4s ease-in-out infinite; }
.lqa-bumpL { animation: lqa-bumpL .6s ease-in-out; }
.lqa-bumpR { animation: lqa-bumpR .6s ease-in-out; }
.lqa-foil { background-image: linear-gradient(110deg, rgba(255,255,255,.05) 0%, rgba(255,215,120,.35) 25%, rgba(120,200,255,.35) 50%, rgba(255,120,220,.35) 75%, rgba(255,255,255,.05) 100%); background-size: 200% 100%; animation: lqa-foil 3s linear infinite; }
.lqa-pulse { animation: lqa-pulse 1.2s ease-out infinite; }
.lqa-confetti { position: fixed; top:-5vh; width:10px; height:14px; border-radius:2px; animation: lqa-confetti linear forwards; pointer-events:none; z-index:60; }
.lqa-tipwrap { position: relative; }
.lqa-tip { position: absolute; z-index: 130; pointer-events: none; opacity: 0; width: max-content; max-width: 250px; transition: opacity .15s ease, transform .15s ease; filter: drop-shadow(0 12px 26px rgba(0,0,0,0.65)); }
.lqa-tipwrap:hover > .lqa-tip, .lqa-tipwrap:focus-within > .lqa-tip { opacity: 1; }
.lqa-tip-top { left: 50%; bottom: calc(100% + 10px); transform: translate(-50%, 6px); }
.lqa-tipwrap:hover > .lqa-tip-top, .lqa-tipwrap:focus-within > .lqa-tip-top { transform: translate(-50%, 0); }
.lqa-tip-bottom { left: 50%; top: calc(100% + 10px); transform: translate(-50%, -6px); }
.lqa-tipwrap:hover > .lqa-tip-bottom, .lqa-tipwrap:focus-within > .lqa-tip-bottom { transform: translate(-50%, 0); }
@media (prefers-reduced-motion: reduce) {
  .lqa-tip { transition: opacity .12s ease !important; }
  .lqa-pop,.lqa-bumpL,.lqa-bumpR,.lqa-foil,.lqa-pulse,.lqa-confetti,.lqa-powerPop,.lqa-rareGlow,.lqa-clash,.lqa-spellflash,.lqa-spellring,.lqa-vsplash,.lqa-slideL,.lqa-slideR,.lqa-vspop,.lqa-roundpop,.lqa-stamp,.lqa-winsplash,.lqa-wincrown,.lqa-namepulse { animation: none !important; }
}
`;
  document.head.appendChild(el);
}

// ----------------------------------------------------------------------------
// Small presentational bits
// ----------------------------------------------------------------------------

function parseColor(parse: number): string {
  if (parse === 100) return "#e5cc80";
  if (parse >= 99) return "#e268a8";
  if (parse >= 95) return "#ff8000";
  if (parse >= 75) return "#a335ee";
  if (parse >= 50) return "#0070dd";
  if (parse >= 25) return "#1eff00";
  return "#9d9d9d";
}

function powerBadgeColor(card: DraftCard): string {
  return card.parse != null ? parseColor(card.parse) : "#9d9d9d";
}

const ROLE_META: Record<string, { label: string; color: string; icon: string }> = {
  "Tank": { label: "Tank", color: "#38bdf8", icon: "🛡" },
  "Healer": { label: "Healer", color: "#34d399", icon: "✚" },
  "Melee DPS": { label: "Melee", color: "#fb923c", icon: "⚔" },
  "Ranged DPS": { label: "Ranged", color: "#fb923c", icon: "🏹" },
};

function RoleBadge({ role, compact }: { role?: string | null; compact?: boolean }) {
  const meta = role ? ROLE_META[role] : null;
  const size = compact ? "text-[9px] px-1 py-[1px] gap-0.5" : "text-[11px] px-1.5 py-0.5 gap-1";
  if (!meta) {
    return <span className={`inline-flex items-center rounded border border-white/15 text-white/45 font-bold ${size}`}>{role || "No role"}</span>;
  }
  return (
    <span
      className={`inline-flex items-center rounded border font-bold ${size}`}
      style={{ color: meta.color, backgroundColor: `${meta.color}1a`, borderColor: `${meta.color}55` }}
    >
      <span aria-hidden>{meta.icon}</span>{meta.label}
    </span>
  );
}

function CardStats({ card, team, compact, hidePower, mut }: { card: DraftCard; team?: DraftCard[]; compact?: boolean; hidePower?: boolean; mut?: Mutator | null }) {
  const parseVal = card.parse ?? 50;
  const parseEstimated = card.parse == null;
  const hasTeam = !!team?.length;
  const power = Math.round(hasTeam ? cardPower(card, team!, [], undefined, mut) : cardBasePower(card));
  const powerColor = parseColor(Math.max(0, Math.min(100, power)));
  const label = compact ? "text-[8px]" : "text-[10px]";
  const big = compact ? "text-lg" : "text-3xl";

  return (
    <div className={`w-full rounded-lg border border-white/15 bg-[#080a10]/90 text-left ${compact ? "px-2 py-1.5" : "px-3 py-2.5"}`}>
      <div className="flex items-end justify-between gap-2">
        <div className="flex flex-col items-start gap-1 min-w-0">
          <RoleBadge role={card.role} compact={compact} />
          <div className="flex items-baseline gap-1">
            <span className={`${label} font-bold uppercase tracking-wider text-white/55`}>Parse</span>
            <span className={`${compact ? "text-sm" : "text-lg"} font-mono font-black leading-none tabular-nums`} style={{ color: parseColor(parseVal) }}>
              {parseVal}{parseEstimated ? "*" : ""}
            </span>
          </div>
        </div>
        {!hidePower && (
          <div className="text-right flex-shrink-0">
            <div className={`${label} font-bold uppercase tracking-wider text-white/55`}>Power</div>
            <span
              className={`${big} font-mono font-black tabular-nums leading-none inline-block mt-0.5 px-2 py-0.5 rounded-md border`}
              style={{ color: powerColor, backgroundColor: `${powerColor}18`, borderColor: `${powerColor}55` }}
            >
              {power}
            </span>
          </div>
        )}
      </div>
      {parseEstimated && !compact && (
        <div className="text-[10px] text-white/40 mt-1.5">* no parse logged — defaults to 50</div>
      )}
    </div>
  );
}

function modifierEffectLabel(m: Modifier): string {
  if (m.combat) return combatLabel(m.combat);
  if (m.aura) return m.aura.desc;
  return "";
}

// A real WoW spell icon (Wowhead CDN), rarity-bordered, with a gem fallback.
function AbilityIcon({ ability, size = 30 }: { ability: Modifier; size?: number }) {
  const [failed, setFailed] = useState(false);
  const rc = RARITY_META[ability.rarity].color;
  useEffect(() => { setFailed(false); }, [ability.icon]);
  return (
    <div
      className="relative flex-shrink-0 grid place-items-center overflow-hidden rounded-md"
      style={{ width: size, height: size, border: `2px solid ${rc}`, boxShadow: `0 0 8px ${rc}66` }}
      title={ability.name}
    >
      {!failed ? (
        <img src={iconUrl(ability.icon)} alt="" className="h-full w-full object-cover" onError={() => setFailed(true)} />
      ) : (
        <span className="font-black leading-none" style={{ color: rc, fontSize: size * 0.5 }} aria-hidden>✦</span>
      )}
    </div>
  );
}

function ModifierPanel({ modifier, compact }: { modifier: Modifier; compact?: boolean }) {
  const rc = RARITY_META[modifier.rarity].color;
  // green for ally buffs, rose for enemy debuffs, amber for combat actives
  const effectColor = modifier.combat ? "#fbbf24" : modifier.aura?.target === "enemy" ? "#fb7185" : "#34d399";
  return (
    <div
      className={`w-full rounded-lg border text-left ${compact ? "px-2 py-1.5" : "px-2.5 py-2"}`}
      style={{ borderColor: `${rc}59`, backgroundColor: `${rc}14` }}
    >
      <div className="flex items-start gap-2">
        <AbilityIcon ability={modifier} size={compact ? 24 : 32} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-1.5">
            <span className={`font-black uppercase tracking-wide leading-snug truncate ${compact ? "text-[10px]" : "text-xs"}`} style={{ color: rc }}>
              {modifier.name}
            </span>
            <span className={`font-black uppercase tracking-wider flex-shrink-0 ${compact ? "text-[7px]" : "text-[8px]"}`} style={{ color: rc }}>
              {RARITY_META[modifier.rarity].label}
            </span>
          </div>
          <div className={`font-bold leading-snug ${compact ? "text-[9px]" : "text-[10px]"}`} style={{ color: effectColor }}>
            {modifierEffectLabel(modifier)}
          </div>
        </div>
      </div>
      <div className={`text-white/55 italic leading-snug ${compact ? "text-[9px] line-clamp-2 mt-1" : "text-[10px] mt-1"}`}>
        {modifier.flavor}
      </div>
    </div>
  );
}

function PlayerAvatar({ icon, className, size = "md" }: { icon?: string | null; className: string; size?: "sm" | "md" | "lg" | "fight" }) {
  const [imgFailed, setImgFailed] = useState(false);
  const color = getClassColor(className);
  const dim = size === "fight"
    ? "h-full w-full rounded-none text-2xl"
    : size === "lg"
      ? "h-[72px] w-[72px] rounded-xl text-lg"
      : size === "sm"
        ? "h-9 w-9 rounded-md text-[11px]"
        : "h-8 w-8 rounded-md text-[11px]";
  const initial = (className || "?").charAt(0).toUpperCase();
  const showImg = Boolean(icon) && !imgFailed;

  useEffect(() => { setImgFailed(false); }, [icon]);

  return (
    <div
      className={`relative flex-shrink-0 grid place-items-center overflow-hidden border border-white/10 bg-[#15161d] ${dim} ${size === "fight" ? "border-0" : ""}`}
      style={size === "fight" ? undefined : { boxShadow: `inset 0 0 0 1px ${color}44` }}
    >
      {showImg ? (
        <img
          src={icon!}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span className="font-bold select-none" style={{ color }} aria-hidden>{initial}</span>
      )}
    </div>
  );
}

function PowerBadge({ power, badgeColor, active, pulseKey, glowColor }: {
  power: number; badgeColor: string; active?: boolean; pulseKey?: number; glowColor?: string | null;
}) {
  return (
    <div
      key={pulseKey}
      className={`absolute -top-2.5 -right-2.5 z-10 ${active ? "lqa-powerPop" : ""}`}
    >
      <div
        className="relative flex items-center justify-center rounded-lg border-[2.5px] font-mono font-black tabular-nums leading-none
          min-w-[42px] px-2 py-1 text-[18px] shadow-[0_4px_16px_rgba(0,0,0,0.9)]"
        style={{
          color: badgeColor,
          backgroundColor: "rgba(6, 8, 14, 0.96)",
          borderColor: badgeColor,
          textShadow: `0 0 10px ${badgeColor}, 0 1px 2px rgba(0,0,0,0.9)`,
          boxShadow: glowColor ? `0 0 0 2px ${glowColor}, 0 0 12px ${glowColor}aa` : undefined,
        }}
      >
        {power}
      </div>
    </div>
  );
}

function FightCard({ card, team, enemy, silenced, mut, active, side, dimmed, wonRound, lostRound, powerPulseKey, typeEdge }: {
  card: DraftCard;
  team: DraftCard[];
  enemy?: DraftCard[];
  silenced?: Set<string>;
  mut?: Mutator | null;
  active?: boolean;
  side?: "L" | "R";
  dimmed?: boolean;
  wonRound?: boolean;
  lostRound?: boolean;
  powerPulseKey?: number;
  typeEdge?: boolean;
}) {
  const color = getClassColor(card.className);
  const ability = card.modifier || null;
  const epic = ability?.rarity === "epic";
  const rc = ability ? RARITY_META[ability.rarity].color : null;
  const silencedSelf = !!(silenced && silenced.has(card.cardId));
  const power = Math.round(cardPower(card, team, enemy || [], silenced, mut));
  const badgeColor = powerBadgeColor(card);

  return (
    <div
      className={`flex flex-col items-center gap-1.5 select-none ${dimmed ? "opacity-35 grayscale" : ""} ${active ? (side === "L" ? "lqa-bumpL" : "lqa-bumpR") : ""}`}
    >
      <div className={`relative ${epic ? "lqa-rareGlow rounded-2xl p-0.5" : ""}`}>
        <div className="relative">
          {active && typeEdge && (
            <span className="absolute -top-2.5 -left-2.5 z-20 text-[9px] font-black uppercase tracking-wide bg-emerald-500 text-black rounded px-1.5 py-0.5 shadow-lg lqa-powerPop" title="Role counters the opponent — small power bonus this round">⚔ Edge</span>
          )}
          <div
            className={`relative h-[96px] w-[96px] rounded-2xl overflow-hidden border-2 bg-[#15161d]
              ${active ? "lqa-clash ring-2 ring-amber-400/80 ring-offset-2 ring-offset-[#0B0E15]" : ""}
              ${wonRound ? "border-emerald-400/80" : ""}
              ${lostRound ? "border-rose-500/50" : ""}`}
            style={{
              borderColor: !active && !wonRound && !lostRound ? (rc || "rgba(255,255,255,0.2)") : undefined,
              boxShadow: rc ? `0 0 22px ${rc}55` : `0 0 18px ${color}33`,
            }}
          >
            {epic && <div className="lqa-foil absolute inset-0 opacity-50 pointer-events-none z-[1]" />}
            <div className="absolute top-0 right-0 z-[2] h-2/5 w-3/5 bg-gradient-to-bl from-black/80 via-black/35 to-transparent pointer-events-none" aria-hidden />
            <PlayerAvatar icon={card.icon} className={card.className} size="fight" />
            {ability && (
              <div className={`absolute bottom-0.5 right-0.5 z-[3] ${silencedSelf ? "grayscale opacity-50" : ""}`}>
                <AbilityIcon ability={ability} size={24} />
              </div>
            )}
            {silencedSelf && (
              <div className="absolute inset-0 z-[4] flex items-center justify-center pointer-events-none" title="Silenced by Counterspell">
                <span className="text-2xl">🚫</span>
              </div>
            )}
          </div>
          <PowerBadge
            power={power}
            badgeColor={badgeColor}
            active={active}
            pulseKey={powerPulseKey}
            glowColor={rc}
          />
        </div>
      </div>
      <div className="max-w-[100px] text-center">
        <div className="text-[11px] font-bold truncate leading-tight" style={{ color }}>{card.name}</div>
        {wonRound && <div className="text-[9px] font-black uppercase tracking-wider text-emerald-400 mt-0.5">Round win</div>}
        {lostRound && <div className="text-[9px] font-black uppercase tracking-wider text-rose-400/80 mt-0.5">Round loss</div>}
      </div>
    </div>
  );
}

// A themed hover/focus tooltip (replaces ugly native title= bubbles). The trigger
// and the tip content are BOTH inline (spans) so it nests anywhere without breaking
// HTML. `side` points it up or down to dodge the clash panel's clipped edges.
function InfoTip({ children, tip, side = "top", className }: {
  children: React.ReactNode; tip: React.ReactNode; side?: "top" | "bottom"; className?: string;
}) {
  return (
    <span className={`lqa-tipwrap inline-flex items-center cursor-help ${className || ""}`} tabIndex={0}>
      {children}
      <span className={`lqa-tip lqa-tip-${side}`} role="tooltip">
        <span className="relative block rounded-xl border border-amber-400/25 bg-[#0B0E15]/[0.98] px-3 py-2 text-left">
          {tip}
          <span
            aria-hidden
            className={`absolute left-1/2 h-2.5 w-2.5 -translate-x-1/2 rotate-45 bg-[#0B0E15] border-amber-400/25 ${side === "top" ? "top-full -mt-[6px] border-b border-r" : "bottom-full -mb-[6px] border-t border-l"}`}
          />
        </span>
      </span>
    </span>
  );
}

// Rich tooltip body for a class ability — name, rarity, effect, flavor.
function AbilityTip({ ability, silenced }: { ability: Modifier; silenced?: boolean }) {
  const rc = RARITY_META[ability.rarity].color;
  const effectColor = ability.combat ? "#fbbf24" : ability.aura?.target === "enemy" ? "#fb7185" : "#34d399";
  return (
    <span className="block w-[208px]">
      <span className="mb-1 flex items-center justify-between gap-2">
        <span className="truncate text-xs font-black" style={{ color: rc }}>{ability.name}</span>
        <span className="flex-shrink-0 text-[8px] font-black uppercase tracking-[0.2em]" style={{ color: rc }}>{RARITY_META[ability.rarity].label}</span>
      </span>
      <span className="block text-[11px] font-bold leading-snug" style={{ color: silenced ? "#71717a" : effectColor }}>
        {silenced ? "🚫 Silenced by Counterspell" : modifierEffectLabel(ability)}
      </span>
      <span className="mt-1 block text-[10px] italic leading-snug text-white/55">{ability.flavor}</span>
    </span>
  );
}

// The stage's global rule, shown in the draft and over the clash.
function MutatorBanner({ mut, compact }: { mut: Mutator | null; compact?: boolean }) {
  if (!mut) return null;
  return (
    <div
      className={`flex items-center gap-2.5 rounded-xl border ${compact ? "px-2.5 py-1.5" : "px-3 py-2.5"}`}
      style={{ borderColor: "rgba(217,70,239,0.4)", background: "linear-gradient(180deg, rgba(217,70,239,0.14) 0%, rgba(217,70,239,0.05) 100%)" }}
    >
      <span className={compact ? "text-lg" : "text-2xl"} aria-hidden>{mut.icon}</span>
      <div className="min-w-0">
        <div className="text-[9px] font-black uppercase tracking-[0.25em] text-fuchsia-300/80 leading-none">Stage Modifier</div>
        <div className={`font-black text-fuchsia-100 leading-tight ${compact ? "text-xs" : "text-sm"}`}>{mut.name}</div>
        {!compact && <div className="text-[11px] text-white/55 leading-tight">{mut.desc}</div>}
      </div>
    </div>
  );
}

// Active composition synergies for a (finished) trio — green chips with rich tips.
function SynergyChips({ cards, center, side = "top" }: { cards: DraftCard[]; center?: boolean; side?: "top" | "bottom" }) {
  const { hits } = teamSynergyResult(cards);
  if (!hits.length) return null;
  return (
    <span className={`inline-flex flex-wrap gap-1.5 ${center ? "justify-center" : ""}`}>
      {hits.map((h) => (
        <InfoTip
          key={h.name}
          side={side}
          tip={
            <span className="block w-[176px]">
              <span className="mb-0.5 flex items-center gap-1 text-xs font-black text-emerald-300"><span aria-hidden>{h.icon}</span>{h.name}</span>
              <span className="block text-[11px] leading-snug text-white/70">{h.desc}</span>
              <span className="mt-1 block font-mono text-[10px] text-emerald-300/80">Team power ×{h.mult.toFixed(2)}</span>
            </span>
          }
        >
          <span className="inline-flex items-center gap-1 text-[10px] font-bold rounded-full border border-emerald-400/40 bg-emerald-500/10 text-emerald-300 px-2 py-0.5">
            <span aria-hidden>{h.icon}</span>{h.name}
            <span className="text-emerald-200/70 font-mono">×{h.mult.toFixed(2)}</span>
          </span>
        </InfoTip>
      ))}
    </span>
  );
}

function Card({ card, onClick, dim, highlight, faded, size = "md", team, hidePower, mut }: {
  card: DraftCard; onClick?: () => void; dim?: boolean; highlight?: boolean; faded?: boolean; size?: "sm" | "md"; team?: DraftCard[]; hidePower?: boolean; mut?: Mutator | null;
}) {
  const color = getClassColor(card.className);
  const ability = card.modifier || null;
  const epic = ability?.rarity === "epic";
  const rc = ability ? RARITY_META[ability.rarity].color : null;
  const compact = size === "sm";
  return (
    <div
      onClick={onClick}
      className={`relative w-full rounded-xl border flex flex-col items-center text-center select-none overflow-hidden transition-all
        ${compact ? `p-2.5 gap-2 ${hidePower ? "min-h-[108px]" : "min-h-[132px]"}` : `p-4 gap-3 ${hidePower ? "min-h-[168px]" : "min-h-[220px]"}`}
        ${onClick ? "cursor-pointer hover:brightness-110 active:scale-[0.98]" : ""}
        ${dim ? "opacity-30 grayscale" : ""}
        ${faded ? "opacity-50" : ""}
        ${highlight ? "ring-2 ring-amber-400" : ""}`}
      style={{
        background: epic ? "#160f1d" : ability ? "#0f1320" : "#121622",
        borderColor: rc ? `${rc}99` : "rgba(255,255,255,0.1)",
        boxShadow: rc ? `0 0 14px ${rc}33` : undefined,
      }}
    >
      {epic && <div className="lqa-foil absolute inset-0 opacity-40 pointer-events-none" />}
      {ability && (
        <span
          className={`absolute top-1.5 right-1.5 z-[2] font-black uppercase tracking-widest rounded px-1 py-px bg-black/55 border ${compact ? "text-[7px]" : "text-[8px]"}`}
          style={{ color: rc!, borderColor: `${rc}59` }}
        >
          {RARITY_META[ability.rarity].label}
        </span>
      )}
      <div className="relative z-[1] flex flex-col items-center gap-2 w-full min-w-0">
        <PlayerAvatar icon={card.icon} className={card.className} size={compact ? "sm" : "lg"} />
        <div className="w-full min-w-0">
          <div className="flex items-center justify-center gap-1 flex-wrap">
            <span className={`font-black truncate max-w-full ${compact ? "text-xs" : "text-base"}`} style={{ color }}>{card.name}</span>
            {card.isAI && <span className="text-[8px] text-rose-300 bg-rose-500/20 px-1 rounded font-bold">AI</span>}
          </div>
          <div className={`truncate ${compact ? "text-[10px] text-white/55" : "text-xs text-white/65"}`}>
            {card.className}{card.spec ? ` · ${card.spec}` : card.role ? ` · ${card.role}` : ""}
          </div>
        </div>
      </div>
      <div className="relative z-[1] w-full">
        <CardStats card={card} team={team} compact={compact} hidePower={hidePower} mut={mut} />
      </div>
      {ability && (
        <div className="relative z-[1] w-full mt-auto">
          <ModifierPanel modifier={ability} compact={compact} />
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Main component
// ----------------------------------------------------------------------------

export interface LootQueueArenaProps {
  mode: "host" | "joiner";
  sessionId: string;
  identity: { id: string; name: string; className?: string | null };
  pool?: ArenaPlayer[]; // host only
  onClose: () => void;
}

const EMPTY_STATE: ArenaState = {
  phase: "lobby", players: [], offers: {}, teams: {}, draftEndsAt: null,
  draftRound: 0, draftingPlayerIds: [], eliminatedIds: [], records: {}, totalStages: 0,
  bracket: null, currentMatchId: null, solo: false, soloStreak: 0, soloFoe: null,
  champion: null, hostName: "", mutator: null,
};

const OFFER_SIZE = 3; // cards shown to choose from on each pick

export default function LootQueueArena({ mode, sessionId, identity, pool, onClose }: LootQueueArenaProps) {
  const isHost = mode === "host";
  const myId = identity.id;
  const reduceMotion = useMemo(
    () => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );

  const ROUND_MS = reduceMotion ? 200 : 850;
  const BANNER_MS = reduceMotion ? 350 : 1300;
  const INTRO_MS = reduceMotion ? 250 : 1700; // "PLAYER VS PLAYER" splash before the clash
  const matchDuration = (n: number) => INTRO_MS + (n + 1) * ROUND_MS + BANNER_MS;

  // ---- shared render state ----
  const [state, setState] = useState<ArenaState>(EMPTY_STATE);
  const [connected, setConnected] = useState(false);
  const [ended, setEnded] = useState<null | "host_left" | "no_session" | "error">(null);
  const [toast, setToast] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // ---- animation state (host + joiner both render the same match_result) ----
  const [activeMatch, setActiveMatch] = useState<MatchResultMsg | null>(null);
  const [revealed, setRevealed] = useState(0); // rounds revealed so far
  const [intro, setIntro] = useState(false); // matchup intro splash showing
  const [showWinner, setShowWinner] = useState(false); // winner reveal splash showing

  // ---- refs ----
  const wsRef = useRef<WebSocket | null>(null);
  const gameRef = useRef<ArenaState>({ ...EMPTY_STATE }); // host-authoritative model
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const advanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const animTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handlerRef = useRef<(msg: any) => void>(() => {});
  const unmountedRef = useRef(false);

  const shareLink = useMemo(() => `${window.location.origin}/arena/${sessionId}`, [sessionId]);

  useEffect(() => { ensureStyles(); }, []);

  // -------- helpers --------
  const send = useCallback((obj: any) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) {
      try { ws.send(JSON.stringify({ ...obj, sessionId })); } catch { /* ignore */ }
    }
  }, [sessionId]);

  const flashToast = useCallback((text: string) => {
    setToast(text);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 1600);
  }, []);

  const clearAnimTimers = useCallback(() => {
    animTimersRef.current.forEach((t) => clearTimeout(t));
    animTimersRef.current = [];
  }, []);

  // Play a match's animation (host & joiner both call this): first the
  // "PLAYER VS PLAYER" splash (INTRO_MS), then reveal each round in turn.
  const playMatch = useCallback((m: MatchResultMsg) => {
    clearAnimTimers();
    setActiveMatch(m);
    setRevealed(0);
    setIntro(true);
    setShowWinner(false);
    animTimersRef.current.push(setTimeout(() => {
      if (!unmountedRef.current) setIntro(false);
    }, INTRO_MS));
    m.rounds.forEach((_r, i) => {
      animTimersRef.current.push(setTimeout(() => {
        if (!unmountedRef.current) setRevealed(i + 1);
      }, INTRO_MS + (i + 1) * ROUND_MS));
    });
    const winnerAt = INTRO_MS + (m.rounds.length + 1) * ROUND_MS;
    animTimersRef.current.push(setTimeout(() => {
      if (!unmountedRef.current) setShowWinner(true);
    }, winnerAt));
    animTimersRef.current.push(setTimeout(() => {
      if (!unmountedRef.current) setShowWinner(false);
    }, winnerAt + BANNER_MS));
  }, [ROUND_MS, INTRO_MS, BANNER_MS, clearAnimTimers]);

  // ===== HOST ENGINE =====================================================
  const snapshot = useCallback((): ArenaState => {
    const g = gameRef.current;
    return {
      phase: g.phase,
      players: [...g.players],
      offers: Object.fromEntries(Object.entries(g.offers).map(([k, v]) => [k, [...v]] as [string, DraftCard[]])),
      teams: Object.fromEntries(Object.entries(g.teams).map(([k, v]) => [k, [...v]] as [string, DraftCard[]])),
      draftEndsAt: g.draftEndsAt,
      draftRound: g.draftRound,
      draftingPlayerIds: [...g.draftingPlayerIds],
      eliminatedIds: [...g.eliminatedIds],
      records: Object.fromEntries(Object.entries(g.records).map(([k, v]) => [k, { ...v }])),
      totalStages: g.totalStages,
      bracket: g.bracket ? { rounds: g.bracket.rounds.map((r) => r.map((m) => ({ ...m }))) } : null,
      currentMatchId: g.currentMatchId,
      solo: g.solo,
      soloStreak: g.soloStreak,
      soloFoe: g.soloFoe ? { ...g.soloFoe } : null,
      champion: g.champion ? { ...g.champion } : null,
      hostName: g.hostName,
      mutator: g.mutator ? { ...g.mutator } : null,
    };
  }, []);

  const syncAndBroadcast = useCallback(() => {
    const snap = snapshot();
    if (!unmountedRef.current) setState(snap);
    if (isHost) send({ type: "arena:state", state: snap });
  }, [isHost, send, snapshot]);

  const emitMatchResult = useCallback((m: MatchResultMsg) => {
    send(m); // server fans out to joiners (sender excluded → host animates locally)
    playMatch(m);
  }, [send, playMatch]);

  // host model holds per-player private draw decks + the AI gauntlet pool
  // (none of this is part of the broadcastable ArenaState).
  const hostModel = useRef<{
    decks: Record<string, DraftCard[]>;
    aiPool: DraftCard[];
    afterRedraft: "gauntlet" | null; // solo gauntlet re-draft flag
  }>({ decks: {}, aiPool: [], afterRedraft: null });

  // Each player drafts from their OWN shuffled deck of fresh card instances.
  // Need OFFER_SIZE × TEAM_SIZE cards (a fresh trio per pick); pad with ringers.
  const buildDeck = useCallback((): DraftCard[] => {
    const deck = (pool || []).map(makeCard);
    shuffle(deck);
    while (deck.length < OFFER_SIZE * TEAM_SIZE) deck.push(makeRinger());
    return deck;
  }, [pool]);

  const dealTrio = useCallback((deck: DraftCard[]): DraftCard[] => {
    const trio: DraftCard[] = [];
    while (trio.length < OFFER_SIZE) trio.push(deck.shift() || makeRinger());
    return trio;
  }, []);

  const drafterIds = useCallback((g: ArenaState = gameRef.current): string[] => {
    if (g.draftingPlayerIds.length) return g.draftingPlayerIds;
    return g.players.filter((p) => !p.isAI).map((p) => p.id);
  }, []);

  const beginDraftPhase = useCallback((opts?: { playerIds?: string[]; isInitial?: boolean }) => {
    const g = gameRef.current;
    const playerIds = opts?.playerIds ?? g.players.filter((p) => !p.isAI).map((p) => p.id);
    if (!playerIds.length) return;

    if (opts?.isInitial) {
      g.draftRound = 0;
      g.eliminatedIds = [];
      g.champion = null;
      g.bracket = { rounds: [] };
      g.soloStreak = 0;
      g.soloFoe = null;
      hostModel.current.afterRedraft = null;
      const ai = (pool || []).map(makeCard); shuffle(ai);
      hostModel.current.aiPool = ai;
      const real = g.players.filter((p) => !p.isAI);
      g.solo = real.length <= 1;
      // Swiss: track everyone's win/loss; run ~ceil(log2(n)) stages → one clear winner.
      g.records = {};
      real.forEach((p) => { g.records[p.id] = { w: 0, l: 0 }; });
      g.totalStages = Math.max(1, Math.ceil(Math.log2(Math.max(2, real.length))));
    } else {
      g.draftRound += 1;
    }

    if (advanceTimerRef.current) { clearTimeout(advanceTimerRef.current); advanceTimerRef.current = null; }
    g.phase = "draft";
    g.draftingPlayerIds = playerIds;
    g.currentMatchId = null;
    // Roll this stage's mutator now so players can draft around it.
    g.mutator = rollMutator();
    g.draftEndsAt = Date.now() + DRAFT_MS;
    setActiveMatch(null);
    setRevealed(0);
    setIntro(false);
    setShowWinner(false);

    playerIds.forEach((id) => {
      g.teams[id] = [];
      const deck = buildDeck();
      hostModel.current.decks[id] = deck;
      g.offers[id] = dealTrio(deck);
    });

    syncAndBroadcast();
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => endDraftRef.current(), DRAFT_MS);
  }, [pool, buildDeck, dealTrio, syncAndBroadcast]);

  // Record a match result into the standings.
  const recordResult = useCallback((winId: string | null, loseId: string | null) => {
    const g = gameRef.current;
    if (winId) { const r = g.records[winId] || (g.records[winId] = { w: 0, l: 0 }); r.w += 1; }
    if (loseId) { const r = g.records[loseId] || (g.records[loseId] = { w: 0, l: 0 }); r.l += 1; }
  }, []);

  // Swiss pairing: sort real players by record (wins desc, losses asc, random
  // tiebreak) and pair them top-down → winners face winners. Odd → a bye (free win).
  const pairStage = useCallback((): BracketMatch[] => {
    const g = gameRef.current;
    const sorted = g.players.filter((p) => !p.isAI).slice().sort((a, b) => {
      const ra = g.records[a.id] || { w: 0, l: 0 };
      const rb = g.records[b.id] || { w: 0, l: 0 };
      if (rb.w !== ra.w) return rb.w - ra.w;
      if (ra.l !== rb.l) return ra.l - rb.l;
      return Math.random() - 0.5;
    });
    const matches: BracketMatch[] = [];
    for (let i = 0; i < sorted.length; i += 2) {
      const a = sorted[i];
      const b = sorted[i + 1];
      const teamA: TeamRef = { ownerId: a.id, ownerName: a.name, cards: g.teams[a.id] || [] };
      const teamB: TeamRef | null = b ? { ownerId: b.id, ownerName: b.name, cards: g.teams[b.id] || [] } : null;
      matches.push({ id: genId("m"), teamA, teamB, winner: null, score: null, done: false });
    }
    return matches;
  }, []);

  // Final standings — deterministic order (wins desc, losses asc, name).
  const rankStandings = useCallback((): LobbyPlayer[] => {
    const g = gameRef.current;
    return g.players.filter((p) => !p.isAI).slice().sort((a, b) => {
      const ra = g.records[a.id] || { w: 0, l: 0 };
      const rb = g.records[b.id] || { w: 0, l: 0 };
      if (rb.w !== ra.w) return rb.w - ra.w;
      if (ra.l !== rb.l) return ra.l - rb.l;
      return a.name.localeCompare(b.name);
    });
  }, []);

  // Seed + start the current Swiss stage's matches.
  const runSwissStage = useCallback(() => {
    const g = gameRef.current;
    if (!g.bracket) g.bracket = { rounds: [] };
    g.bracket.rounds[g.draftRound] = pairStage();
    g.bracket.rounds = g.bracket.rounds.slice(0, g.draftRound + 1);
    g.phase = "bracket";
    g.currentMatchId = null;
    syncAndBroadcast();
    advanceTimerRef.current = setTimeout(() => advanceBracketRef.current(), reduceMotion ? 200 : 600);
  }, [pairStage, reduceMotion, syncAndBroadcast]);

  const buildAiTeam = useCallback((): TeamRef => {
    const cards: DraftCard[] = [];
    for (let i = 0; i < TEAM_SIZE; i++) {
      cards.push(hostModel.current.aiPool.shift() || makeRinger());
    }
    return { ownerId: genId("ai"), ownerName: AI_FOE_NAMES[Math.floor(Math.random() * AI_FOE_NAMES.length)], cards, isAI: true };
  }, []);

  const nextFoeRef = useRef<() => void>(() => {});
  nextFoeRef.current = () => {
    const g = gameRef.current;
    const hostTeam: TeamRef = { ownerId: myId, ownerName: identity.name, cards: g.teams[myId] || [] };
    const foe = buildAiTeam();
    g.soloFoe = foe;
    g.currentMatchId = "solo";
    syncAndBroadcast();
    const res = resolveMatch(hostTeam.cards, foe.cards, g.mutator);
    emitMatchResult({ type: "arena:match_result", sessionId, matchId: genId("solo"), teamA: hostTeam, teamB: foe, rounds: res.rounds, winner: res.winner, score: res.score, mutator: g.mutator });
    advanceTimerRef.current = setTimeout(() => {
      if (res.winner === "A") {
        g.soloStreak += 1;
        g.currentMatchId = null;
        syncAndBroadcast();
        hostModel.current.afterRedraft = "gauntlet";
        beginDraftPhase({ playerIds: [myId] });
      } else {
        g.phase = "gameover";
        g.currentMatchId = null;
        g.champion = null; // solo: streak is the result, not a champion
        syncAndBroadcast();
      }
    }, matchDuration(res.rounds.length));
  };

  const startGauntlet = useCallback(() => {
    const g = gameRef.current;
    g.phase = "bracket";
    g.solo = true;
    g.soloStreak = 0;
    syncAndBroadcast();
    advanceTimerRef.current = setTimeout(() => nextFoeRef.current(), reduceMotion ? 150 : 500);
  }, [reduceMotion, syncAndBroadcast]);

  const endDraft = useCallback(() => {
    if (draftTimerRef.current) { clearTimeout(draftTimerRef.current); draftTimerRef.current = null; }
    const g = gameRef.current;
    if (g.phase !== "draft") return;
    const ids = drafterIds(g);
    // auto-fill short teams from their current trio, then their deck, then ringers
    for (const id of ids) {
      const team = g.teams[id] || (g.teams[id] = []);
      const offer = g.offers[id] || [];
      const deck = hostModel.current.decks[id] || [];
      while (team.length < TEAM_SIZE) {
        team.push(offer.shift() || deck.shift() || makeRinger());
      }
      g.offers[id] = [];
    }
    g.draftEndsAt = null;
    const after = hostModel.current.afterRedraft;
    hostModel.current.afterRedraft = null;

    if (after === "gauntlet") {
      g.phase = "bracket";
      syncAndBroadcast();
      advanceTimerRef.current = setTimeout(() => nextFoeRef.current(), reduceMotion ? 150 : 500);
    } else if (g.solo) {
      startGauntlet();
    } else {
      runSwissStage(); // multiplayer: run this stage's record-paired matches
    }
  }, [drafterIds, runSwissStage, startGauntlet, reduceMotion, syncAndBroadcast]);

  const endDraftRef = useRef(endDraft);
  endDraftRef.current = endDraft;

  const startDraft = useCallback(() => {
    const g = gameRef.current;
    g.teams = {};
    g.offers = {};
    hostModel.current.decks = {};
    beginDraftPhase({ isInitial: true });
  }, [beginDraftPhase]);

  // A player picks one card from THEIR OWN trio; the whole trio then refreshes.
  // No contention — each player has a private deck, so no "snatched".
  const handlePick = useCallback((cardId: string, byId: string) => {
    const g = gameRef.current;
    if (g.phase !== "draft") return;
    if (!drafterIds(g).includes(byId)) return;
    const team = g.teams[byId] || (g.teams[byId] = []);
    if (team.length >= TEAM_SIZE) return;
    const offer = g.offers[byId] || [];
    const card = offer.find((c) => c.cardId === cardId);
    if (!card) return; // not in their current trio (stale tap) — ignore, no penalty
    team.push(card);
    if (team.length >= TEAM_SIZE) {
      g.offers[byId] = []; // done drafting
    } else {
      const deck = hostModel.current.decks[byId] || (hostModel.current.decks[byId] = buildDeck());
      g.offers[byId] = dealTrio(deck); // refresh ALL THREE
    }
    const everyoneDone = drafterIds(g).every((id) => (g.teams[id]?.length || 0) >= TEAM_SIZE);
    syncAndBroadcast();
    if (everyoneDone) endDraftRef.current();
  }, [buildDeck, dealTrio, drafterIds, syncAndBroadcast]);

  // Run the current Swiss stage match-by-match, updating records. When the stage
  // is done: next stage (everyone re-drafts) or finish → ranked standings.
  const advanceBracketRef = useRef<() => void>(() => {});
  advanceBracketRef.current = () => {
    const g = gameRef.current;
    if (!g.bracket) return;
    const stage = g.bracket.rounds[g.draftRound] || [];
    const next = stage.find((m) => !m.done);

    if (next) {
      if (next.teamA && !next.teamB) { // bye → free win, no opponent
        next.winner = "A"; next.score = [0, 0]; next.done = true;
        recordResult(next.teamA.ownerId, null);
        g.currentMatchId = null;
        syncAndBroadcast();
        advanceTimerRef.current = setTimeout(() => advanceBracketRef.current(), reduceMotion ? 120 : 450);
        return;
      }
      if (next.teamA && next.teamB) {
        const res = resolveMatch(next.teamA.cards, next.teamB.cards, g.mutator);
        g.currentMatchId = next.id;
        syncAndBroadcast();
        emitMatchResult({ type: "arena:match_result", sessionId, matchId: next.id, teamA: next.teamA, teamB: next.teamB, rounds: res.rounds, winner: res.winner, score: res.score, mutator: g.mutator });
        advanceTimerRef.current = setTimeout(() => {
          next.winner = res.winner; next.score = res.score; next.done = true;
          const winId = res.winner === "A" ? next.teamA!.ownerId : next.teamB!.ownerId;
          const loseId = res.winner === "A" ? next.teamB!.ownerId : next.teamA!.ownerId;
          recordResult(winId, loseId);
          g.currentMatchId = null;
          syncAndBroadcast();
          advanceBracketRef.current();
        }, matchDuration(res.rounds.length));
        return;
      }
      next.done = true; // both null (shouldn't happen)
      advanceBracketRef.current();
      return;
    }

    // stage complete → another stage (re-draft everyone) or finish
    if (g.draftRound + 1 < g.totalStages) {
      beginDraftPhase(); // bumps draftRound, re-drafts all real players
    } else {
      const ranked = rankStandings();
      const top = ranked[0];
      g.phase = "gameover";
      g.champion = top ? { ownerId: top.id, ownerName: top.name, cards: g.teams[top.id] || [] } : null;
      g.currentMatchId = null;
      syncAndBroadcast();
    }
  };

  const addPlayer = useCallback((p: { id: string; name: string; className?: string | null; isAI?: boolean }) => {
    const g = gameRef.current;
    if (!p || !p.id) return;
    const existing = g.players.find((x) => x.id === p.id);
    if (existing) { existing.name = p.name || existing.name; return; }
    g.players.push({ id: p.id, name: p.name || "Raider", className: p.className || "Unknown", isHost: false, isAI: p.isAI });
    if (g.phase === "draft" && !g.teams[p.id]) g.teams[p.id] = [];
    if (!p.isAI && !g.records[p.id]) g.records[p.id] = { w: 0, l: 0 };
  }, []);

  const removePlayer = useCallback((id: string) => {
    const g = gameRef.current;
    g.players = g.players.filter((p) => p.id !== id);
    // Only safe to drop a team before the bracket is seeded (§6e).
    if (g.phase === "lobby" || g.phase === "draft") delete g.teams[id];
  }, []);

  const restart = useCallback(() => {
    const g = gameRef.current;
    // keep lobby, reshuffle everything else (§6c — no data saved)
    g.phase = "lobby";
    g.offers = {};
    g.teams = {};
    g.bracket = null;
    g.currentMatchId = null;
    g.champion = null;
    g.solo = false;
    g.soloStreak = 0;
    g.soloFoe = null;
    g.draftEndsAt = null;
    g.draftRound = 0;
    g.draftingPlayerIds = [];
    g.eliminatedIds = [];
    g.records = {};
    g.totalStages = 0;
    g.mutator = null;
    hostModel.current.decks = {};
    hostModel.current.aiPool = [];
    hostModel.current.afterRedraft = null;
    setActiveMatch(null);
    setRevealed(0);
    setIntro(false);
    setShowWinner(false);
    syncAndBroadcast();
  }, [syncAndBroadcast]);

  // -------- message handler (kept in a ref so it never goes stale) --------
  handlerRef.current = (msg: any) => {
    if (!msg || typeof msg.type !== "string") return;
    switch (msg.type) {
      case "arena:state":
        if (!isHost) setState(msg.state as ArenaState);
        break;
      case "arena:match_result":
        if (!isHost) playMatch(msg as MatchResultMsg);
        break;
      case "arena:join":
        if (isHost && msg.player) {
          addPlayer(msg.player);
          const g = gameRef.current;
          const pid = msg.player.id;
          // If someone joins mid-draft, deal them their own trio so they can play.
          if (g.phase === "draft" && drafterIds(g).includes(pid) && (g.teams[pid]?.length || 0) < TEAM_SIZE && (g.offers[pid]?.length || 0) === 0) {
            const deck = hostModel.current.decks[pid] || (hostModel.current.decks[pid] = buildDeck());
            g.offers[pid] = dealTrio(deck);
          }
          syncAndBroadcast();
        }
        break;
      case "arena:pick":
        if (isHost) handlePick(msg.cardId, msg.byId);
        break;
      case "arena:player_left":
        if (isHost && msg.id) { removePlayer(msg.id); syncAndBroadcast(); }
        break;
      case "arena:host_left":
        if (!isHost) { setEnded("host_left"); }
        break;
      case "arena:no_session":
        if (!isHost) setEnded("no_session");
        break;
      default:
        break;
    }
  };

  // -------- socket lifecycle --------
  useEffect(() => {
    unmountedRef.current = false;
    let opened = false;

    // Connect straight to the backend. In dev API_URL is http://localhost:4000
    // (CRA's dev-server proxy does NOT forward raw WebSocket upgrades); in prod
    // API_URL is "" so we fall back to the same origin that serves the app.
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = API_URL ? API_URL.replace(/^https?:/, proto) : `${proto}//${window.location.host}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      if (ws !== wsRef.current) return; // superseded by a re-mount (StrictMode) — ignore
      opened = true;
      setConnected(true);
      if (isHost) {
        // become host: register the session, seat ourselves as player #1
        gameRef.current = { ...EMPTY_STATE, teams: {} };
        gameRef.current.players = [{ id: myId, name: identity.name, className: identity.className || "Unknown", isHost: true }];
        gameRef.current.hostName = identity.name;
        send({ type: "arena:create" });
        syncAndBroadcast();
      } else {
        send({ type: "arena:join", player: { id: myId, name: identity.name, className: identity.className || "Unknown" } });
      }
    };

    ws.onmessage = (ev) => {
      if (ws !== wsRef.current) return;
      let msg: any;
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (!msg || typeof msg.type !== "string") return;
      if (msg.type.indexOf("arena:") !== 0) return;           // ignore non-arena traffic on this socket
      if (msg.sessionId && msg.sessionId !== sessionId) return; // ignore other arena sessions
      handlerRef.current(msg);
    };

    ws.onerror = () => { if (ws === wsRef.current && !opened) setEnded("error"); };
    ws.onclose = () => {
      if (ws !== wsRef.current) return; // a superseded/discarded socket — ignore
      setConnected(false);
      // Host's own socket dropping = the game is over for everyone anyway.
      if (!unmountedRef.current && !isHost && !ended) {
        // joiner lost connection (or host's session vanished)
        setEnded((prev) => prev || "host_left");
      }
    };

    return () => {
      unmountedRef.current = true;
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      animTimersRef.current.forEach((t) => clearTimeout(t));
      animTimersRef.current = [];
      // Detach handlers first so closing a still-CONNECTING socket (StrictMode
      // double-mount in dev) can't fire onerror/onclose and flip the UI to error.
      try {
        ws.onopen = null; ws.onmessage = null; ws.onerror = null; ws.onclose = null;
        ws.close();
      } catch { /* ignore */ }
      if (wsRef.current === ws) wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, isHost]);

  // -------- draft countdown ticker (display only; host owns the real timeout) --------
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    if (state.phase !== "draft" || !state.draftEndsAt) return;
    const iv = setInterval(() => setNowTick(Date.now()), 250);
    return () => clearInterval(iv);
  }, [state.phase, state.draftEndsAt]);

  // ---- escape closes ----
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // ========================================================================
  // RENDER
  // ========================================================================

  const myTeam = state.teams[myId] || [];
  const draftSecondsLeft = state.draftEndsAt ? Math.max(0, Math.ceil((state.draftEndsAt - nowTick) / 1000)) : 0;

  const tryPick = (cardId: string) => {
    if (state.phase !== "draft") return;
    if (state.draftingPlayerIds.length && !state.draftingPlayerIds.includes(myId)) return;
    if (state.eliminatedIds.includes(myId)) return;
    if (myTeam.length >= TEAM_SIZE) { flashToast("Your team is full"); return; }
    if (isHost) handlePick(cardId, myId);
    else send({ type: "arena:pick", cardId, byId: myId });
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6" style={{ background: "rgba(4,6,10,0.92)", backdropFilter: "blur(6px)" }}>
      {/* The bracket phase spreads to a 3-column battle layout, so it needs the extra room. */}
      <div className={`relative w-full ${state.phase === "bracket" && !state.solo ? "max-w-7xl" : "max-w-4xl"} max-h-full overflow-y-auto rounded-2xl border border-white/10 bg-[#0B0E15] shadow-2xl`}>
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-3 border-b border-white/10 bg-[#0B0E15]/95 backdrop-blur">
          <div className="flex items-center gap-2">
            <span className="text-lg">⚔️</span>
            <div>
              <div className="text-sm font-black tracking-wide text-amber-400 uppercase">Loot Queue Arena</div>
              <div className="text-[10px] text-white/35 -mt-0.5">{isHost ? "You are the host" : "Joined a host's game"} · {connected ? "live" : "connecting…"}</div>
            </div>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-lg border border-white/10 text-white/60 hover:text-white hover:bg-white/10 flex items-center justify-center" title="Close (Esc)">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="p-5">
          {ended ? (
            <EndScreen kind={ended} onClose={onClose} />
          ) : !connected ? (
            <div className="py-16 text-center text-white/50 text-sm">Connecting to the arena…</div>
          ) : state.phase === "lobby" ? (
            <Lobby
              state={state} isHost={isHost} myId={myId} shareLink={shareLink} copied={copied}
              onCopy={() => { try { navigator.clipboard.writeText(shareLink); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ } }}
              onStart={startDraft}
            />
          ) : state.phase === "draft" ? (
            <Draft state={state} myId={myId} myTeam={myTeam} secondsLeft={draftSecondsLeft} onPick={tryPick} />
          ) : state.phase === "bracket" ? (
            <BracketView state={state} myId={myId} activeMatch={activeMatch} revealed={revealed} intro={intro} introMs={INTRO_MS} showWinner={showWinner} bannerMs={BANNER_MS} reduceMotion={reduceMotion} />
          ) : (
            <GameOver state={state} isHost={isHost} myId={myId} onRestart={restart} onClose={onClose} reduceMotion={reduceMotion} />
          )}
        </div>
      </div>

      {/* Snatch / info toast */}
      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[110] px-4 py-2 rounded-lg bg-rose-600/90 text-white text-sm font-bold shadow-2xl border border-rose-300/40">
          {toast}
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Sub-views
// ----------------------------------------------------------------------------

function Lobby({ state, isHost, myId, shareLink, copied, onCopy, onStart }: {
  state: ArenaState; isHost: boolean; myId: string; shareLink: string; copied: boolean; onCopy: () => void; onStart: () => void;
}) {
  return (
    <div className="space-y-5">
      <div className="text-center space-y-1">
        <h2 className="text-xl font-black text-white">Lobby</h2>
        <p className="text-xs text-white/40">Draft a fresh 3-card team each round. Stack a comp for synergy bonuses and play to the stage modifier. Nothing is saved.</p>
      </div>

      {isHost && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-amber-300/80 mb-1.5">Share this link to invite raiders</div>
          <div className="flex items-center gap-2">
            <input readOnly value={shareLink} className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-xs text-white/70 font-mono truncate" onFocus={(e) => e.currentTarget.select()} />
            <button onClick={onCopy} className="px-3 py-2 rounded-lg bg-amber-500 text-black text-xs font-bold hover:bg-amber-400 transition-colors whitespace-nowrap">
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
        </div>
      )}

      <div>
        <div className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-2">Players ({state.players.length})</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {state.players.map((p) => (
            <div key={p.id} className="lqa-pop flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
              <div className="w-1.5 h-6 rounded-full" style={{ backgroundColor: getClassColor(p.className) }} />
              <div className="min-w-0">
                <div className="text-sm font-bold truncate" style={{ color: getClassColor(p.className) }}>{p.name}{p.id === myId ? " (you)" : ""}</div>
                <div className="text-[10px] text-white/40 truncate">{p.className}{p.isHost ? " · host" : ""}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {isHost ? (
        <button onClick={onStart} disabled={state.players.length < 1} className="w-full py-3 rounded-xl bg-amber-500 text-black font-black uppercase tracking-wide hover:bg-amber-400 transition-colors disabled:opacity-40">
          {state.players.length <= 1 ? "Start (solo gauntlet)" : `Start draft · ${state.players.length} players`}
        </button>
      ) : (
        <div className="text-center text-sm text-white/40 py-2">Waiting for the host to start…</div>
      )}
    </div>
  );
}

function RivalStrip({ state, myId }: { state: ArenaState; myId: string }) {
  const rivals = state.players.filter((p) => {
    if (p.id === myId) return false;
    if (state.draftingPlayerIds.length) return state.draftingPlayerIds.includes(p.id);
    return true;
  });
  return (
    <div className="flex flex-wrap gap-1.5">
      {rivals.map((p) => {
        const count = state.teams[p.id]?.length || 0;
        return (
          <span key={p.id} className="text-[10px] px-2 py-1 rounded-full border border-white/10 bg-white/[0.03]" style={{ color: getClassColor(p.className) }}>
            {p.name} <span className="text-white/40">{count}/{TEAM_SIZE}</span>
          </span>
        );
      })}
    </div>
  );
}

function Draft({ state, myId, myTeam, secondsLeft, onPick }: {
  state: ArenaState; myId: string; myTeam: DraftCard[]; secondsLeft: number; onPick: (cardId: string) => void;
}) {
  const isEliminated = state.eliminatedIds.includes(myId);
  const isDrafting = !state.draftingPlayerIds.length || state.draftingPlayerIds.includes(myId);
  const draftTitle = state.draftRound === 0
    ? "Draft"
    : state.solo
      ? `Redraft · Fight ${state.soloStreak + 1}`
      : `Redraft · Stage ${state.draftRound + 1}`;

  if (isEliminated || !isDrafting) {
    return (
      <div className="space-y-4 py-8 text-center">
        <h2 className="text-lg font-black text-white">{draftTitle}</h2>
        <div className="text-4xl">{isEliminated ? "💀" : "⏳"}</div>
        <p className="text-sm text-white/50 max-w-sm mx-auto">
          {isEliminated
            ? "You're out — survivors are picking their next team."
            : "Waiting for survivors to finish drafting…"}
        </p>
        {secondsLeft > 0 && (
          <div className={`text-xl font-black font-mono ${secondsLeft <= 5 ? "text-rose-400" : "text-amber-400/70"}`}>{secondsLeft}s</div>
        )}
      </div>
    );
  }

  const full = myTeam.length >= TEAM_SIZE;
  const offer = state.offers[myId] || [];
  const pickNum = Math.min(myTeam.length + 1, TEAM_SIZE);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-black text-white">
          {draftTitle} <span className="text-white/30 text-sm font-medium">— pick one, the rest reshuffle</span>
        </h2>
        <div className={`text-2xl font-black font-mono ${secondsLeft <= 5 ? "text-rose-400" : "text-amber-400"}`}>{secondsLeft}s</div>
      </div>

      {state.mutator && <MutatorBanner mut={state.mutator} />}

      {state.players.length > 1 && <RivalStrip state={state} myId={myId} />}

      {/* My team */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[10px] font-bold uppercase tracking-widest text-white/40">Your team ({myTeam.length}/{TEAM_SIZE})</div>
          <SynergyChips cards={myTeam} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => (
            myTeam[i]
              ? <Card key={myTeam[i].cardId} card={myTeam[i]} team={myTeam} hidePower />
              : <div key={i} className="min-h-[168px] rounded-xl border border-dashed border-white/10 flex items-center justify-center text-[11px] text-white/25 uppercase tracking-widest">Empty</div>
          ))}
        </div>
        {myTeam.length === TEAM_SIZE && teamSynergyResult(myTeam).hits.length === 0 && (
          <div className="mt-1.5 text-[10px] text-white/30">No comp synergy — try stacking a role, a class, or going full melee/ranged for a power bonus.</div>
        )}
      </div>

      {/* Your private choice — pick ONE; the whole trio refreshes for the next pick */}
      <div>
        <div className="text-[10px] font-bold uppercase tracking-widest text-amber-300/70 mb-1.5">
          {full ? "Locked in — waiting for the others" : `Pick ${pickNum} of ${TEAM_SIZE} — choose one`}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {full ? (
            <div className="col-span-full text-center text-white/30 text-sm py-6">Team locked. Waiting…</div>
          ) : offer.length === 0 ? (
            <div className="col-span-full text-center text-white/30 text-sm py-6">Dealing…</div>
          ) : (
            offer.map((c) => {
              const prospective = [...myTeam, c];
              const prospectiveSyn = prospective.length === TEAM_SIZE ? teamSynergyResult(prospective).hits : [];
              return (
                <div key={c.cardId} className="lqa-pop space-y-1.5">
                  <Card card={c} onClick={() => onPick(c.cardId)} team={prospective} hidePower />
                  {prospectiveSyn.length > 0 && (
                    <div className="flex flex-col items-center gap-0.5">
                      <span className="text-[8px] font-black uppercase tracking-[0.2em] text-emerald-300/60">Completes</span>
                      <div className="flex flex-wrap gap-1 justify-center">
                        {prospectiveSyn.map((h) => (
                          <span key={h.name} className="inline-flex items-center gap-0.5 text-[9px] font-bold rounded-full border border-emerald-400/40 bg-emerald-500/10 text-emerald-300 px-1.5 py-0.5">
                            <span aria-hidden>{h.icon}</span>{h.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function TeamColumn({ team, enemy, silenced, mut, side, activeIndex, bumping, dimmed, roundWinnerSide, powerPulseKey, typeEdge }: {
  team: TeamRef; enemy: DraftCard[]; silenced?: Set<string>; mut?: Mutator | null; side: "L" | "R"; activeIndex: number; bumping: boolean; dimmed: boolean;
  roundWinnerSide?: "A" | "B" | null; powerPulseKey?: number; typeEdge?: boolean;
}) {
  const mySide = side === "L" ? "A" : "B";
  const total = Math.round(teamPower(team.cards, enemy, silenced, mut));
  const accent = mySide === "A" ? "#fbbf24" : "#fb7185";
  const teamColor = team.isAI ? "#fb7185" : accent;
  return (
    <div className={`flex-1 flex flex-col min-w-0 ${dimmed ? "opacity-40" : ""}`}>
      <div
        className="rounded-xl border px-3 py-2.5 mb-5 text-center w-full shrink-0"
        style={{ borderColor: `${accent}55`, background: `linear-gradient(180deg, ${accent}1a 0%, ${accent}08 100%)` }}
      >
        <div className="text-[9px] font-bold uppercase tracking-[0.25em] text-white/35 mb-1">
          {mySide === "A" ? "Team A" : "Team B"}
        </div>
        <div className="text-base sm:text-lg font-black truncate leading-tight" style={{ color: teamColor }}>
          {team.ownerName}{team.isAI ? " 🤖" : ""}
        </div>
        <InfoTip
          side="bottom"
          className="mt-1.5"
          tip={
            <span className="block w-[200px]">
              <span className="mb-0.5 block text-xs font-black" style={{ color: accent }}>Team Power</span>
              <span className="block text-[11px] leading-snug text-white/70">Each card's parse adjusted by ally buffs &amp; enemy debuffs, then multiplied by any comp synergy. The fight's odds come straight from this.</span>
            </span>
          }
        >
          <span
            className="inline-flex items-center gap-1 text-[11px] font-mono font-black tabular-nums px-2 py-0.5 rounded-md border"
            style={{ color: accent, backgroundColor: `${accent}14`, borderColor: `${accent}44` }}
          >
            <span aria-hidden>⚡</span>{total}
          </span>
        </InfoTip>
        <div className="mt-1.5">
          <SynergyChips cards={team.cards} center side="bottom" />
        </div>
      </div>
      <div className="flex flex-col items-center gap-5 w-full px-1 overflow-visible border-t border-white/[0.06] pt-4">
        {team.cards.map((c, i) => {
          const isFighter = bumping && i === activeIndex;
          const wonRound = isFighter && roundWinnerSide === mySide;
          const lostRound = isFighter && roundWinnerSide != null && roundWinnerSide !== mySide;
          return (
            <FightCard
              key={c.cardId}
              card={c}
              team={team.cards}
              enemy={enemy}
              silenced={silenced}
              mut={mut}
              active={isFighter}
              side={side}
              dimmed={dimmed && !isFighter}
              wonRound={wonRound}
              lostRound={lostRound}
              powerPulseKey={isFighter ? powerPulseKey : undefined}
              typeEdge={isFighter ? typeEdge : undefined}
            />
          );
        })}
      </div>
    </div>
  );
}

// Left rail — the team YOU drafted this stage, pinned for the whole combat phase so
// you can keep admiring/reviewing your build even when other players are fighting.
function MyTeamRail({ state, myId, activeMatch, mut }: {
  state: ArenaState; myId: string; activeMatch: MatchResultMsg | null; mut: Mutator | null;
}) {
  const me = state.players.find((p) => p.id === myId);
  const cards = state.teams[myId] || [];
  const name = me?.name || "You";
  const color = getClassColor(me?.className);
  const power = Math.round(teamPower(cards, [], undefined, mut));
  const rec = state.records[myId];
  const fightingNow = !!activeMatch && (activeMatch.teamA.ownerId === myId || activeMatch.teamB.ownerId === myId);

  return (
    <div className="w-full lg:w-64 lg:shrink-0 lg:sticky lg:top-3">
      <div
        className="rounded-2xl border p-3"
        style={{ borderColor: `${color}44`, background: `linear-gradient(180deg, ${color}12 0%, ${color}05 100%)` }}
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="min-w-0">
            <div className="text-[9px] font-bold uppercase tracking-[0.25em] text-white/35">Your Team</div>
            <div className="text-sm font-black truncate" style={{ color }}>{name}</div>
          </div>
          <InfoTip
            side="bottom"
            tip={<span className="block w-[180px] text-[11px] leading-snug text-white/70">Base power of the team you drafted this stage, before this fight's matchup buffs &amp; debuffs.</span>}
          >
            <span className="inline-flex items-center gap-1 text-[11px] font-mono font-black tabular-nums px-2 py-0.5 rounded-md border" style={{ color, backgroundColor: `${color}14`, borderColor: `${color}44` }}>
              <span aria-hidden>⚡</span>{power}
            </span>
          </InfoTip>
        </div>

        {fightingNow && (
          <div className="mb-2 text-center text-[10px] font-black uppercase tracking-widest text-amber-300 lqa-pulse rounded-md border border-amber-400/40 bg-amber-400/10 py-1">
            ⚔ In the arena now
          </div>
        )}

        {rec && (
          <div className="mb-2 text-center text-xs font-mono font-black tabular-nums">
            <span className="text-emerald-400">{rec.w}</span><span className="text-white/30">–</span><span className="text-rose-400">{rec.l}</span>
            <span className="ml-1 font-sans text-[10px] font-bold uppercase tracking-wide text-white/35">record</span>
          </div>
        )}

        {cards.length > 0 ? (
          <>
            <div className="flex flex-col gap-2">
              {cards.map((c) => (
                <Card key={c.cardId} card={c} size="sm" team={cards} mut={mut} />
              ))}
            </div>
            <div className="mt-2 flex justify-center">
              <SynergyChips cards={cards} center side="bottom" />
            </div>
          </>
        ) : (
          <div className="py-6 text-center text-xs text-white/35">No team drafted this stage.</div>
        )}
      </div>
    </div>
  );
}

// A single collapsible enemy squad row in the right rail.
function EnemySquadRow({ player, cards, mut, fighting, defaultOpen }: {
  player: LobbyPlayer; cards: DraftCard[]; mut: Mutator | null; fighting: boolean; defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  const color = player.isAI ? "#fb7185" : getClassColor(player.className);
  const power = Math.round(teamPower(cards, [], undefined, mut));
  return (
    <div
      className={`rounded-xl border overflow-hidden ${fighting ? "lqa-pulse" : ""}`}
      style={{ borderColor: fighting ? "#fbbf2466" : "rgba(255,255,255,0.1)", background: fighting ? "rgba(251,191,36,0.05)" : "rgba(255,255,255,0.02)" }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-2.5 py-2 text-left hover:bg-white/[0.04] transition-colors"
      >
        <span className="w-1.5 h-6 rounded-full shrink-0" style={{ backgroundColor: color }} />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1">
            <span className="text-sm font-bold truncate" style={{ color }}>{player.name}{player.isAI ? " 🤖" : ""}</span>
            {fighting && <span className="text-[8px] font-black uppercase tracking-widest text-amber-300 shrink-0">· fighting</span>}
          </span>
          <span className="block text-[10px] text-white/40 truncate">{player.className || "Unknown"}</span>
        </span>
        <span className="inline-flex items-center gap-1 text-[10px] font-mono font-black tabular-nums px-1.5 py-0.5 rounded border shrink-0" style={{ color, backgroundColor: `${color}14`, borderColor: `${color}44` }}>
          <span aria-hidden>⚡</span>{power}
        </span>
        <svg className={`w-3.5 h-3.5 text-white/40 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && (
        <div className="px-2.5 pb-2.5 pt-1 border-t border-white/5">
          {cards.length > 0 ? (
            <>
              <div className="flex flex-col gap-2">
                {cards.map((c) => (
                  <Card key={c.cardId} card={c} size="sm" team={cards} mut={mut} />
                ))}
              </div>
              <div className="mt-2 flex justify-center">
                <SynergyChips cards={cards} center side="bottom" />
              </div>
            </>
          ) : (
            <div className="py-3 text-center text-[11px] text-white/30">No team drafted yet.</div>
          )}
        </div>
      )}
    </div>
  );
}

// Right rail — every other squad in the lobby, collapsed by default. Click to expand
// and inspect a rival's full build. The two teams in the current fight are flagged.
function EnemySquadsRail({ state, myId, activeMatch, mut }: {
  state: ArenaState; myId: string; activeMatch: MatchResultMsg | null; mut: Mutator | null;
}) {
  const fightingIds = new Set(
    activeMatch ? [activeMatch.teamA.ownerId, activeMatch.teamB.ownerId] : []
  );
  // Every other team (humans + any AI foes), fighting squads first, then by team power.
  const foes = state.players
    .filter((p) => p.id !== myId)
    .map((p) => ({ player: p, cards: state.teams[p.id] || [], fighting: fightingIds.has(p.id) }))
    .sort((a, b) => {
      if (a.fighting !== b.fighting) return a.fighting ? -1 : 1;
      return teamPower(b.cards, [], undefined, mut) - teamPower(a.cards, [], undefined, mut);
    });

  return (
    <div className="w-full lg:w-72 lg:shrink-0 lg:sticky lg:top-3">
      <div className="rounded-2xl border border-white/10 bg-black/30 p-3">
        <div className="text-[9px] font-bold uppercase tracking-[0.25em] text-white/35 mb-2">
          Enemy Squads {foes.length > 0 && <span className="text-white/25">({foes.length})</span>}
        </div>
        {foes.length > 0 ? (
          <div className="space-y-2 lg:max-h-[70vh] lg:overflow-y-auto lg:pr-1">
            {foes.map(({ player, cards, fighting }) => (
              <EnemySquadRow key={player.id} player={player} cards={cards} mut={mut} fighting={fighting} defaultOpen={fighting} />
            ))}
          </div>
        ) : (
          <div className="py-6 text-center text-xs text-white/35">No other squads yet.</div>
        )}
      </div>
    </div>
  );
}

function BracketView({ state, myId, activeMatch, revealed, intro, introMs, showWinner, bannerMs, reduceMotion }: {
  state: ArenaState; myId: string; activeMatch: MatchResultMsg | null; revealed: number; intro: boolean; introMs: number; showWinner: boolean; bannerMs: number; reduceMotion: boolean;
}) {
  // index of the round entry currently shown (entries include rerolled-away ones)
  const entryIdx = Math.max(0, revealed - 1);
  const rounds = activeMatch?.rounds ?? [];
  // The rule the fight resolved under — prefer the one stamped on the match.
  const mut = activeMatch?.mutator ?? state.mutator ?? null;
  const allRevealed = activeMatch ? revealed >= rounds.length : false;
  // Scoreboard counts ONLY entries that count (re-rolled-away attempts don't).
  const aWins = rounds.slice(0, revealed).filter((r) => !r.rerolledAway && r.winner === "A").length;
  const bWins = rounds.slice(0, revealed).filter((r) => !r.rerolledAway && r.winner === "B").length;
  const totalRounds = rounds.filter((r) => !r.rerolledAway).length;
  const playedRounds = rounds.slice(0, revealed).filter((r) => !r.rerolledAway).length;
  const bumping = !reduceMotion && !!activeMatch && !showWinner && !intro;
  const curEntry = activeMatch && revealed > 0 ? rounds[entryIdx] : undefined;
  const roundWinnerSide = bumping && curEntry ? curEntry.winner : null;

  // Which card-duel this entry shows (pair index), drives the bumping fighters.
  const matchBasesData = activeMatch ? matchBases(activeMatch.teamA.cards, activeMatch.teamB.cards, mut) : null;
  const silenced = matchBasesData?.silenced;
  const pairIdx = curEntry?.pair ?? entryIdx;
  const aIdx = activeMatch ? Math.min(pairIdx, activeMatch.teamA.cards.length - 1) : 0;
  const bIdx = activeMatch ? Math.min(pairIdx, activeMatch.teamB.cards.length - 1) : 0;
  const duelEdge = bumping && activeMatch
    ? roleEdge(activeMatch.teamA.cards[aIdx]?.role, activeMatch.teamB.cards[bIdx]?.role)
    : 0;

  // Pre-fight stats shown around the VS: total power + bo3 win chance.
  const powerA = matchBasesData ? Math.round(matchBasesData.baseA) : 0;
  const powerB = matchBasesData ? Math.round(matchBasesData.baseB) : 0;
  const pctA = activeMatch ? Math.round(matchWinChance(activeMatch.teamA.cards, activeMatch.teamB.cards, mut) * 100) : 50;
  const pctB = 100 - pctA;
  const favoredA = pctA >= pctB;
  // The ability that fired this round → big spell flash.
  const proc = curEntry?.proc;

  // Player colors/records for the splash + round callouts.
  const nameColor = (t: TeamRef) => (t.isAI ? "#fb7185" : getClassColor(state.players.find((p) => p.id === t.ownerId)?.className));
  const recordOf = (t: TeamRef) => { const r = state.records[t.ownerId]; return r ? ` · ${r.w}-${r.l}` : ""; };
  // "{winner} takes round N" callout, popped per counted round.
  const roundCallout = !intro && activeMatch && curEntry && !curEntry.rerolledAway
    ? {
        name: curEntry.winner === "A" ? activeMatch.teamA.ownerName : activeMatch.teamB.ownerName,
        color: curEntry.winner === "A" ? "#fbbf24" : "#fb7185",
        n: playedRounds,
      }
    : null;

  const winnerTeam = activeMatch && activeMatch.winner === "A" ? activeMatch.teamA : activeMatch?.teamB;
  const loserTeam = activeMatch && activeMatch.winner === "A" ? activeMatch.teamB : activeMatch?.teamA;
  const winnerColor = winnerTeam ? nameColor(winnerTeam) : "#fbbf24";

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-black text-white">{state.solo ? "Gauntlet" : "Bracket"}</h2>
        {state.solo && <div className="text-sm font-black text-emerald-400">Win streak: {state.soloStreak} 🔥</div>}
      </div>

      {mut && <MutatorBanner mut={mut} />}

      {/* Combat layout — your team (left) · the live battle (middle) · enemy squads (right).
          Players wanted to keep eyeing what they built even while other people fight. */}
      <div className="flex flex-col lg:flex-row gap-4 lg:items-start">
        {!state.solo && <MyTeamRail state={state} myId={myId} activeMatch={activeMatch} mut={mut} />}

        {/* The clash */}
        <div className="flex-1 min-w-0">
      {activeMatch ? (
        <div className="relative rounded-2xl border border-white/10 bg-black/30 p-4 overflow-hidden">
          <div className="flex items-center justify-center gap-3 mb-2">
            <span key={`a${aWins}`} className="text-xl font-black font-mono text-amber-400 lqa-powerPop">{aWins}</span>
            <span className="text-[10px] uppercase tracking-widest text-white/30">round {Math.min(playedRounds || 1, totalRounds)} / {totalRounds}</span>
            <span key={`b${bWins}`} className="text-xl font-black font-mono text-rose-400 lqa-powerPop">{bWins}</span>
          </div>

          {/* Per-round winner callout — "{player} takes round N" */}
          <div className="h-6 flex items-center justify-center mb-1">
            {roundCallout && (
              <span key={`rc-${revealed}`} className="lqa-roundpop text-[13px] font-black px-3 py-0.5 rounded-full border" style={{ color: roundCallout.color, borderColor: `${roundCallout.color}66`, background: `${roundCallout.color}1a` }}>
                Round {roundCallout.n} → {roundCallout.name}
              </span>
            )}
          </div>

          {/* Matchup intro — player vs player splash before the clash */}
          {intro && (
            <div className="lqa-vsplash absolute inset-0 z-40 flex flex-col items-center justify-center gap-5 px-4" style={{ ["--lqa-intro" as any]: `${introMs}ms`, background: "rgba(8,10,16,0.95)" }}>
              <div className="text-[10px] font-black uppercase tracking-[0.35em] text-amber-400/70">
                {state.solo ? "Gauntlet · Fight begins" : "Match begins"}
              </div>
              <div className="flex items-center justify-center gap-4 sm:gap-6 w-full max-w-lg">
                <div className="lqa-slideL flex-1 min-w-0 text-right">
                  <div
                    className="lqa-namepulse text-2xl sm:text-3xl font-black truncate px-2 py-1 rounded-lg border"
                    style={{ color: nameColor(activeMatch.teamA), borderColor: `${nameColor(activeMatch.teamA)}44`, background: `${nameColor(activeMatch.teamA)}12` }}
                  >
                    {activeMatch.teamA.ownerName}
                  </div>
                  <div className="text-[11px] text-white/45 font-mono mt-2">⚡ {powerA}{recordOf(activeMatch.teamA)}</div>
                </div>
                <div className="lqa-vspop text-4xl sm:text-6xl font-black text-white flex-shrink-0 px-1" style={{ textShadow: "0 0 20px rgba(245,158,11,0.8), 0 0 6px rgba(245,158,11,1)" }}>VS</div>
                <div className="lqa-slideR flex-1 min-w-0 text-left">
                  <div
                    className="lqa-namepulse text-2xl sm:text-3xl font-black truncate px-2 py-1 rounded-lg border"
                    style={{ color: nameColor(activeMatch.teamB), borderColor: `${nameColor(activeMatch.teamB)}44`, background: `${nameColor(activeMatch.teamB)}12` }}
                  >
                    {activeMatch.teamB.ownerName}
                  </div>
                  <div className="text-[11px] text-white/45 font-mono mt-2">⚡ {powerB}{recordOf(activeMatch.teamB)}</div>
                </div>
              </div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/30">Get ready…</div>
            </div>
          )}

          {/* Winner reveal splash after the final round */}
          {showWinner && activeMatch && winnerTeam && loserTeam && (
            <div className="lqa-winsplash absolute inset-0 z-50 flex flex-col items-center justify-center px-4 overflow-hidden" style={{ ["--lqa-banner" as any]: `${bannerMs}ms`, background: "radial-gradient(circle at center, rgba(15,23,42,0.95) 0%, rgba(0,0,0,0.98) 100%)" }}>
              {/* Decorative spinning background rays */}
              <div className="absolute inset-0 z-0 flex items-center justify-center opacity-30 mix-blend-screen pointer-events-none">
                <div className="absolute w-[150%] h-[150%] animate-[spin_30s_linear_infinite]" style={{ background: `repeating-conic-gradient(from 0deg, transparent 0deg, transparent 10deg, ${winnerColor}40 10deg, ${winnerColor}40 20deg)` }} />
                <div className="absolute inset-0" style={{ background: "radial-gradient(circle, transparent 20%, rgba(0,0,0,0.95) 70%)" }} />
              </div>

              <div className="lqa-wincrown relative z-10 flex flex-col items-center w-full max-w-lg p-6 sm:p-8 rounded-3xl border border-white/10 shadow-[0_0_100px_rgba(0,0,0,0.8)] backdrop-blur-md" style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(0,0,0,0.5) 100%)", boxShadow: `0 20px 50px -10px ${winnerColor}40, inset 0 1px 1px rgba(255,255,255,0.2)` }}>
                {/* Glowing Crown Icon */}
                <div className="relative flex justify-center mb-4 sm:mb-6">
                  <div className="absolute inset-0 blur-[30px] opacity-60 rounded-full" style={{ background: winnerColor }} />
                  <div className="relative text-5xl sm:text-6xl drop-shadow-[0_0_15px_rgba(255,255,255,0.5)] transform -rotate-12 transition-transform hover:rotate-0">👑</div>
                </div>

                <div className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.4em] mb-1 sm:mb-2 text-transparent bg-clip-text bg-gradient-to-b from-white to-white/50">
                  Total Victory
                </div>

                <div className="text-3xl sm:text-5xl font-black truncate px-2 py-2 w-full text-center tracking-tight" style={{ color: winnerColor, textShadow: `0 0 30px ${winnerColor}80, 0 2px 4px rgba(0,0,0,0.8)` }}>
                  {winnerTeam.ownerName}
                </div>

                <div className="flex items-center gap-3 sm:gap-4 mt-4 sm:mt-6 w-full px-8">
                  <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent to-white/20" />
                  <div className="lqa-stamp text-xl sm:text-2xl font-black italic uppercase tracking-[0.2em] text-transparent bg-clip-text" style={{ backgroundImage: `linear-gradient(to bottom, #fff, ${winnerColor})`, filter: `drop-shadow(0 0 10px ${winnerColor}60)` }}>
                    WINNER
                  </div>
                  <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent to-white/20" />
                </div>

                <div className="mt-6 sm:mt-8 flex flex-col items-center p-3 sm:p-4 rounded-xl w-full bg-black/40 border border-white/5 shadow-inner">
                  <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-white/40 mb-2 sm:mb-3">Final Score</div>
                  <div className="flex items-center justify-center gap-4 sm:gap-8 w-full">
                    {/* Team A — left side, same as the battle layout */}
                    <div className={`flex flex-col items-center flex-1 min-w-0 ${activeMatch.winner === "A" ? "" : "opacity-60"}`}>
                      <div className="text-sm sm:text-base font-bold text-white/90 truncate w-full text-center">{activeMatch.teamA.ownerName}</div>
                      <div className="text-3xl sm:text-4xl font-black font-mono tabular-nums mt-1 leading-none" style={{ color: activeMatch.winner === "A" ? nameColor(activeMatch.teamA) : "rgba(255,255,255,0.5)", textShadow: activeMatch.winner === "A" ? `0 0 15px ${nameColor(activeMatch.teamA)}50` : "none" }}>{activeMatch.score[0]}</div>
                    </div>
                    <div className="text-lg sm:text-xl font-black text-white/20 italic mb-2">vs</div>
                    {/* Team B — right side, same as the battle layout */}
                    <div className={`flex flex-col items-center flex-1 min-w-0 ${activeMatch.winner === "B" ? "" : "opacity-60"}`}>
                      <div className="text-sm sm:text-base font-bold text-white/90 truncate w-full text-center">{activeMatch.teamB.ownerName}</div>
                      <div className="text-3xl sm:text-4xl font-black font-mono tabular-nums mt-1 leading-none" style={{ color: activeMatch.winner === "B" ? nameColor(activeMatch.teamB) : "rgba(255,255,255,0.5)", textShadow: activeMatch.winner === "B" ? `0 0 15px ${nameColor(activeMatch.teamB)}50` : "none" }}>{activeMatch.score[1]}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Spell flash — the ability that fired this round */}
          {proc && (
            <div key={`${entryIdx}-${proc.name}`} className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none">
              <div className="lqa-spellring absolute h-28 w-28 rounded-full border-2" style={{ borderColor: RARITY_META[proc.rarity].color }} />
              <div className="lqa-spellflash flex flex-col items-center gap-1">
                <AbilityIcon ability={{ id: "", name: proc.name, flavor: "", classKey: "", rarity: proc.rarity, icon: proc.icon }} size={64} />
                <span className="text-sm font-black px-3 py-0.5 rounded-full bg-black/70 border" style={{ color: RARITY_META[proc.rarity].color, borderColor: `${RARITY_META[proc.rarity].color}88` }}>{proc.name}</span>
              </div>
            </div>
          )}

          <div className="flex items-stretch gap-5 sm:gap-6 py-3">
            <TeamColumn team={activeMatch.teamA} enemy={activeMatch.teamB.cards} silenced={silenced} mut={mut} side="L" activeIndex={aIdx} bumping={bumping} dimmed={allRevealed && !bumping && activeMatch.winner !== "A"} roundWinnerSide={roundWinnerSide} powerPulseKey={revealed} typeEdge={duelEdge > 0} />

            {/* Center odds gauge — gorgeous clash UI */}
            <div className="flex flex-col items-center justify-center flex-shrink-0 self-center w-28 sm:w-36 px-1 relative group">
              
              {/* VS Clash diamond */}
              <div className="lqa-tipwrap relative flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 z-10 mb-1 cursor-help transition-transform hover:scale-105">
                <div className="absolute inset-0 bg-black/80 rounded-xl rotate-45 border border-white/10 shadow-[0_0_30px_rgba(0,0,0,0.6)] backdrop-blur-sm" />
                <div className="absolute inset-0 rounded-xl rotate-45 border-2 border-white/10 transition-all duration-700 mix-blend-overlay"
                  style={{
                    borderColor: favoredA ? "#fbbf24" : "#fb7185",
                    boxShadow: favoredA ? "inset 0 0 20px rgba(251,191,36,0.4), 0 0 15px rgba(251,191,36,0.3)" : "inset 0 0 20px rgba(251,113,133,0.4), 0 0 15px rgba(251,113,133,0.3)"
                  }}
                />
                <span className="text-lg sm:text-xl font-black italic tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-white to-white/40 drop-shadow-lg z-10 pr-1">VS</span>
                <span className="lqa-tip lqa-tip-bottom" role="tooltip">
                  <span className="relative block rounded-xl border border-amber-400/25 bg-[#0B0E15]/[0.98] px-3 py-2 text-left">
                    <span className="mb-0.5 block text-xs font-black text-white">Win chance</span>
                    <span className="block text-[11px] leading-snug text-white/70">
                      Odds of taking the best-of-3, from team power plus role type-counters. It only sets the coin's weight — upsets still happen.
                    </span>
                    <span aria-hidden className="absolute left-1/2 bottom-full -mb-[6px] h-2.5 w-2.5 -translate-x-1/2 rotate-45 border-t border-l border-amber-400/25 bg-[#0B0E15]" />
                  </span>
                </span>
              </div>

              {/* Angled Battle Meter */}
              <div className="relative w-full h-4 sm:h-5 rounded bg-black/50 border border-white/10 shadow-[inset_0_2px_10px_rgba(0,0,0,0.8)] transform -skew-x-12 z-0 -mt-3 overflow-hidden">
                <div className="absolute inset-0 flex items-stretch">
                  {/* Team A side */}
                  <div className="relative h-full transition-[width] duration-700 ease-out flex items-center justify-end overflow-hidden" 
                       style={{ width: `${pctA}%` }}>
                    <div className="absolute inset-0 bg-gradient-to-r from-amber-600/80 to-amber-400" />
                    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0IiBoZWlnaHQ9IjQiPjxyZWN0IHdpZHRoPSI0IiBoZWlnaHQ9IjQiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIi8+PC9zdmc+')] opacity-20 mix-blend-overlay" />
                    {pctA >= pctB && <div className="absolute right-0 inset-y-0 w-12 bg-gradient-to-l from-white/50 to-transparent mix-blend-overlay" />}
                  </div>
                  
                  {/* Team B side */}
                  <div className="relative h-full transition-[width] duration-700 ease-out flex items-center justify-start overflow-hidden"
                       style={{ width: `${pctB}%` }}>
                    <div className="absolute inset-0 bg-gradient-to-r from-rose-400 to-rose-600/80" />
                    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0IiBoZWlnaHQ9IjQiPjxyZWN0IHdpZHRoPSI0IiBoZWlnaHQ9IjQiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIi8+PC9zdmc+')] opacity-20 mix-blend-overlay" />
                    {pctB > pctA && <div className="absolute left-0 inset-y-0 w-12 bg-gradient-to-r from-white/50 to-transparent mix-blend-overlay" />}
                  </div>
                </div>
                
                {/* Center Clash Line */}
                <div className="absolute top-0 bottom-0 left-[calc(50%-1px)] w-[2px] bg-white/80 shadow-[0_0_8px_rgba(255,255,255,0.9)] z-10" />
              </div>

              {/* Stats readout */}
              <div className="flex items-start justify-between w-full mt-3 px-0.5">
                {/* A Stats */}
                <div className="flex flex-col items-start gap-1">
                  <div className="flex items-baseline gap-1">
                    <span className="text-sm sm:text-base font-black text-amber-400 tabular-nums leading-none" style={{ textShadow: pctA >= pctB ? "0 0 16px rgba(251,191,36,0.6)" : "0 2px 4px rgba(0,0,0,0.8)" }}>{pctA}%</span>
                  </div>
                  <div className="text-[9px] sm:text-[10px] font-mono font-bold text-white/50 tabular-nums bg-black/40 px-1.5 py-0.5 rounded border border-white/5 shadow-inner leading-none flex items-center gap-1">
                    <span className="text-amber-400/70">⚡</span>{powerA}
                  </div>
                </div>

                {/* B Stats */}
                <div className="flex flex-col items-end gap-1">
                  <div className="flex items-baseline gap-1 flex-row-reverse">
                    <span className="text-sm sm:text-base font-black text-rose-400 tabular-nums leading-none" style={{ textShadow: pctB > pctA ? "0 0 16px rgba(251,113,133,0.6)" : "0 2px 4px rgba(0,0,0,0.8)" }}>{pctB}%</span>
                  </div>
                  <div className="text-[9px] sm:text-[10px] font-mono font-bold text-white/50 tabular-nums bg-black/40 px-1.5 py-0.5 rounded border border-white/5 shadow-inner leading-none flex items-center gap-1">
                    {powerB}<span className="text-rose-400/70">⚡</span>
                  </div>
                </div>
              </div>
            </div>

            <TeamColumn team={activeMatch.teamB} enemy={activeMatch.teamA.cards} silenced={silenced} mut={mut} side="R" activeIndex={bIdx} bumping={bumping} dimmed={allRevealed && !bumping && activeMatch.winner !== "B"} roundWinnerSide={roundWinnerSide} powerPulseKey={revealed} typeEdge={duelEdge < 0} />
          </div>
          {allRevealed && !showWinner && !bumping && (
            <div className="mt-4 text-center text-sm font-black lqa-pop" style={{ color: nameColor(activeMatch.winner === "A" ? activeMatch.teamA : activeMatch.teamB) }}>
              {(activeMatch.winner === "A" ? activeMatch.teamA : activeMatch.teamB).ownerName} wins {Math.max(activeMatch.score[0], activeMatch.score[1])}–{Math.min(activeMatch.score[0], activeMatch.score[1])}!
            </div>
          )}

          {/* Per-team score breakdown — how each total was calculated vs this foe */}
          <div className="grid grid-cols-2 gap-3 mt-3">
            <TeamScorecard team={activeMatch.teamA} enemy={activeMatch.teamB.cards} silenced={silenced} mut={mut} accent="#fbbf24" />
            <TeamScorecard team={activeMatch.teamB} enemy={activeMatch.teamA.cards} silenced={silenced} mut={mut} accent="#fb7185" />
          </div>

          <div className="mt-3 pt-2 border-t border-white/5 flex items-center justify-center">
            <InfoTip
              side="top"
              tip={
                <span className="block w-[224px] text-[11px] leading-snug text-white/70">
                  Each round's fighters check roles: if one role beats the other in this cycle, that side gets up to a {Math.round(ROLE_EDGE * 100)}% power nudge for the round. Enough to tilt a coin-flip — never enough to decide it.
                </span>
              }
            >
              <span className="inline-flex flex-wrap items-center justify-center gap-1 text-[10px] font-bold">
                <span className="mr-1 uppercase tracking-wider text-white/35">Type edge</span>
                {["Tank", "Melee DPS", "Ranged DPS", "Healer", "Tank"].map((role, i, arr) => {
                  const meta = ROLE_META[role];
                  return (
                    <React.Fragment key={i}>
                      <span
                        className="inline-flex items-center gap-0.5 rounded-full border px-1.5 py-0.5"
                        style={{ color: meta.color, borderColor: `${meta.color}55`, backgroundColor: `${meta.color}1a` }}
                      >
                        <span aria-hidden>{meta.icon}</span>{meta.label}
                      </span>
                      {i < arr.length - 1 && <span className="text-white/25">▸</span>}
                    </React.Fragment>
                  );
                })}
              </span>
            </InfoTip>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-white/10 bg-black/30 p-6 text-center text-white/40 text-sm">Seeding…</div>
      )}
        </div>

        {!state.solo && <EnemySquadsRail state={state} myId={myId} activeMatch={activeMatch} mut={mut} />}
      </div>

      {/* Live standings (Swiss — everyone keeps playing, paired by record) */}
      {!state.solo && <Standings state={state} myId={myId} />}

      {/* Stage history */}
      {!state.solo && state.bracket && state.bracket.rounds.length > 0 && (
        <div className="flex gap-4 overflow-x-auto pb-2">
          {state.bracket.rounds.map((round, ri) => (
            <div key={ri} className="flex-shrink-0 space-y-2 min-w-[150px]">
              <div className="text-[9px] font-bold uppercase tracking-widest text-white/30">Stage {ri + 1}{ri === state.draftRound ? " · now" : ""}</div>
              {round.map((m) => (
                <div key={m.id} className={`rounded-lg border p-2 text-xs ${m.id === state.currentMatchId ? "border-amber-400 lqa-pulse" : "border-white/10"} bg-white/[0.02]`}>
                  <MatchTeamRow teamRef={m.teamA} win={m.done && m.winner === "A"} lose={m.done && m.winner === "B"} />
                  <div className="h-px bg-white/5 my-1" />
                  {m.teamB ? <MatchTeamRow teamRef={m.teamB} win={m.done && m.winner === "B"} lose={m.done && m.winner === "A"} /> : <div className="text-[10px] text-white/25 italic">bye (free win)</div>}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Per-team breakdown of how the total power was reached, vs THIS opponent:
// base parse → net buffs/debuffs (allies + enemy auras) → final.
function TeamScorecard({ team, enemy, silenced, mut, accent }: { team: TeamRef; enemy: DraftCard[]; silenced?: Set<string>; mut?: Mutator | null; accent: string }) {
  const cards = team.cards;
  const total = Math.round(teamPower(cards, enemy, silenced, mut));
  const synergy = teamSynergyResult(cards);
  return (
    <div className="rounded-lg border border-white/10 bg-black/40 p-2.5">
      <div className="text-[9px] font-bold uppercase tracking-widest mb-1.5 truncate" style={{ color: accent }}>{team.ownerName}</div>
      <div className="space-y-1">
        {cards.map((c) => {
          const bd = cardPowerBreakdown(c, cards, enemy, silenced, mut);
          const base = Math.round(bd.base);
          const final = Math.round(bd.final);
          const buffed = final > base;
          const debuffed = final < base;
          const a = c.modifier;
          const isSilenced = !!(silenced && silenced.has(c.cardId));
          return (
            <div key={c.cardId} className="flex items-center justify-between gap-2 text-[11px] leading-tight">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="truncate font-bold" style={{ color: getClassColor(c.className) }}>{c.name}</span>
                <RoleBadge role={c.role} compact />
                {a && (
                  <InfoTip side="top" tip={<AbilityTip ability={a} silenced={isSilenced} />}>
                    <span style={{ color: isSilenced ? "#71717a" : RARITY_META[a.rarity].color }}>{isSilenced ? "🚫" : "✦"}</span>
                  </InfoTip>
                )}
              </div>
              <div className="flex items-center gap-1 font-mono tabular-nums flex-shrink-0">
                <span className="text-white/45">{base}</span>
                {(buffed || debuffed) && <span className="text-white/25">→</span>}
                <span className="font-black" style={{ color: buffed ? "#34d399" : debuffed ? "#fb7185" : accent }}>{final}</span>
              </div>
            </div>
          );
        })}
      </div>
      {synergy.hits.length > 0 && (
        <div className="mt-2 pt-1.5 border-t border-white/10 space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-[9px] font-bold uppercase tracking-wide text-emerald-300/70">Synergy</span>
            <span className="font-mono font-black tabular-nums text-[11px] text-emerald-300">×{synergy.mult.toFixed(2)}</span>
          </div>
          <SynergyChips cards={cards} />
        </div>
      )}
      <div className="mt-2 pt-1.5 border-t border-white/10 flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-wide text-white/45">Total</span>
        <span className="font-mono font-black tabular-nums text-sm" style={{ color: accent }}>⚡ {total}</span>
      </div>
    </div>
  );
}

// Swiss standings — wins/losses per player, ranked.
function Standings({ state, myId, title }: { state: ArenaState; myId: string; title?: string }) {
  const ranked = state.players.filter((p) => !p.isAI).slice().sort((a, b) => {
    const ra = state.records[a.id] || { w: 0, l: 0 };
    const rb = state.records[b.id] || { w: 0, l: 0 };
    if (rb.w !== ra.w) return rb.w - ra.w;
    if (ra.l !== rb.l) return ra.l - rb.l;
    return a.name.localeCompare(b.name);
  });
  const medal = ["🥇", "🥈", "🥉"];
  const stageLabel = state.totalStages ? ` · stage ${Math.min(state.draftRound + 1, state.totalStages)}/${state.totalStages}` : "";
  return (
    <div className="rounded-xl border border-white/10 bg-black/30 p-3">
      <div className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-2">{title || "Standings"}{!title && stageLabel}</div>
      <div className="space-y-1">
        {ranked.map((p, i) => {
          const r = state.records[p.id] || { w: 0, l: 0 };
          return (
            <div key={p.id} className={`flex items-center justify-between gap-2 rounded px-2 py-1 ${p.id === myId ? "bg-white/[0.06]" : ""}`}>
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-5 text-center text-xs flex-shrink-0">{medal[i] || i + 1}</span>
                <span className="truncate text-sm font-bold" style={{ color: getClassColor(p.className) }}>{p.name}{p.id === myId ? " (you)" : ""}</span>
              </div>
              <span className="font-mono font-black text-xs tabular-nums flex-shrink-0">
                <span className="text-emerald-400">{r.w}</span><span className="text-white/30">–</span><span className="text-rose-400">{r.l}</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MatchTeamRow({ teamRef, win, lose }: { teamRef: TeamRef | null; win?: boolean; lose?: boolean }) {
  if (!teamRef) return <div className="text-[10px] text-white/25 italic">TBD</div>;
  return (
    <div className={`flex items-center justify-between gap-2 ${lose ? "opacity-40" : ""}`}>
      <span className="truncate font-bold" style={{ color: teamRef.isAI ? "#fb7185" : win ? "#fbbf24" : "#e5e7eb" }}>{teamRef.ownerName}</span>
      {win && <span className="text-amber-400">✓</span>}
    </div>
  );
}

function Confetti({ reduceMotion }: { reduceMotion: boolean }) {
  if (reduceMotion) return null;
  const colors = ["#fbbf24", "#f59e0b", "#34d399", "#38bdf8", "#f472b6", "#a78bfa"];
  const pieces = Array.from({ length: 40 }, (_, i) => i);
  return (
    <>
      {pieces.map((i) => (
        <span key={i} className="lqa-confetti" style={{
          left: `${Math.random() * 100}%`,
          background: colors[i % colors.length],
          animationDuration: `${2 + Math.random() * 2}s`,
          animationDelay: `${Math.random() * 0.6}s`,
        }} />
      ))}
    </>
  );
}

function GameOver({ state, isHost, myId, onRestart, onClose, reduceMotion }: {
  state: ArenaState; isHost: boolean; myId: string; onRestart: () => void; onClose: () => void; reduceMotion: boolean;
}) {
  const solo = state.solo;
  const celebrate = solo ? state.soloStreak >= 3 : true; // confetti only on a real win
  return (
    <div className="relative space-y-5 py-4 text-center">
      {celebrate && <Confetti reduceMotion={reduceMotion} />}

      {solo ? (
        <>
          <div className="text-5xl">{state.soloStreak >= 3 ? "🏆" : "💀"}</div>
          <h2 className="text-2xl font-black text-amber-400">Gauntlet over</h2>
          <p className="text-white/60 text-sm">You survived a win streak of <span className="font-black text-emerald-400">{state.soloStreak}</span> before the queue popped.</p>
        </>
      ) : (
        <>
          <div className="text-5xl">🏆</div>
          <h2 className="text-2xl font-black text-amber-400">{state.champion ? `${state.champion.ownerName} wins!` : "Final standings"}</h2>
          <div className="max-w-sm mx-auto text-left">
            <Standings state={state} myId={myId} title="Final standings" />
          </div>
        </>
      )}

      <div className="flex items-center justify-center gap-2 pt-2">
        {isHost && (
          <button onClick={onRestart} className="px-5 py-2.5 rounded-xl bg-amber-500 text-black font-black uppercase tracking-wide hover:bg-amber-400 transition-colors">
            Start again
          </button>
        )}
        <button onClick={onClose} className="px-5 py-2.5 rounded-xl border border-white/15 text-white/70 font-bold hover:bg-white/10 transition-colors">Close</button>
      </div>
      {!isHost && <div className="text-xs text-white/30">Waiting for the host to start another round…</div>}
    </div>
  );
}

function EndScreen({ kind, onClose }: { kind: "host_left" | "no_session" | "error"; onClose: () => void }) {
  const text = kind === "no_session"
    ? "This game no longer exists — the host has already closed it."
    : kind === "error"
      ? "Couldn't reach the arena. Try again later."
      : "Host has left the game.";
  return (
    <div className="py-16 text-center space-y-4">
      <div className="text-4xl">👋</div>
      <h2 className="text-xl font-black text-white">{text}</h2>
      <p className="text-xs text-white/40">Nothing was saved — that's the whole point.</p>
      <button onClick={onClose} className="px-5 py-2.5 rounded-xl bg-amber-500 text-black font-black uppercase tracking-wide hover:bg-amber-400 transition-colors">Close</button>
    </div>
  );
}
